/**
 * atelier-cinema.js — THE CINEMATIC GREEK CANVAS.
 *
 * The homepage is one continuous oil painting. Browser scroll drives a
 * master timeline; this engine repaints a fixed full-viewport pair of
 * canvases as a PURE FUNCTION of scroll position, so reverse scroll
 * replays the film backward for free and reduced motion renders
 * finished static frames.
 *
 * Architecture:
 *   - Six scenes, keyed by the live DOM order of
 *     #MainContent [data-atl-scene] (robust to missing / reordered /
 *     duplicated sections in the editor).
 *   - Per scene, three parallax layers (far / mid / near) are painted
 *     ONCE per resize into offscreen canvases (LRU-cached); a frame is
 *     a handful of drawImage blits plus a light dynamic pass (the
 *     Muse, doves, embers, motes, transition effects).
 *   - Every scene boundary owns a DISTINCT painted transition: loaded
 *     brush wipe, page turn, pigment wash, fresco expansion, water
 *     ripple. Never a plain crossfade.
 *   - The recurring Muse walks with your scroll, idles when you stop,
 *     performs per-scene choreography and always keeps to the margin
 *     opposite the text panel — she never covers copy.
 *   - Value ladder: cotton dominant, noir as ink, maroon masses, ONE
 *     cherry event per scene. Visible brushwork everywhere: every
 *     surface is built from tapered strokes, daubs and dry scumbles —
 *     no smooth gradients pretending to be paint.
 *
 * Contracts honoured: Atelier.register lifecycle, html.atl-cinema-on,
 * window.AtelierAudio?.* hooks, atl:study-open/close calming, DPR caps
 * (1.5 desktop / 1.25 <=768px), AtelierCinemaDebug under ?atlDebug=1,
 * clean teardown on section unload.
 */
(() => {
  const boot = (A) => {
    A.register('cinema', (root) => {
      const doc = document;
      const htmlEl = doc.documentElement;
      const { clamp, lerp, hash } = A.helpers;

      /* Without the scene shell there is nothing to film — fail open
         (the editorial page simply renders; no html flag is set). */
      const sceneRoot = doc.getElementById('MainContent') || doc;
      if (!sceneRoot.querySelector('[data-atl-scene]')) return () => {};

      /* ---- The two stacked canvases (created here, removed on teardown) */
      const back = doc.createElement('canvas');
      const dyn = doc.createElement('canvas');
      [back, dyn].forEach((c) => {
        c.className = 'atl-painting__canvas';
        c.style.position = 'absolute';
        c.style.inset = '0';
        c.style.width = '100%';
        c.style.height = '100%';
        c.style.display = 'block';
      });
      root.appendChild(back);
      root.appendChild(dyn);
      const bctx = back.getContext('2d', { alpha: false });
      const dctx = dyn.getContext('2d');
      if (!bctx || !dctx) {
        back.remove();
        dyn.remove();
        return () => {};
      }

      htmlEl.classList.add('atl-cinema-on');

      /* =============================================================
         1. PALETTE — the four pigments and honest mixes of them only.
         Every colour below is either a pigment, a sanctioned derived
         tone, or mix(a, b, t) of two of them.
         ============================================================= */
      const hex2rgb = (hx) => {
        const n = parseInt(hx.slice(1), 16);
        return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
      };
      const mix = (a, b, t) => {
        const ca = hex2rgb(a);
        const cb = hex2rgb(b);
        return (
          'rgb(' +
          Math.round(lerp(ca[0], cb[0], t)) + ',' +
          Math.round(lerp(ca[1], cb[1], t)) + ',' +
          Math.round(lerp(ca[2], cb[2], t)) + ')'
        );
      };
      const COTTON = '#edebdd';
      const CHERRY = '#810100';
      const MAROON = '#630000';
      const NOIR = '#1b1717';
      const C = {
        cotton: COTTON,
        cherry: CHERRY,
        maroon: MAROON,
        noir: NOIR,
        parchment: '#f4f2e8',
        bone: '#ddd8c6',
        rose: '#d9a9a2',
        umber: '#3a2422',
        noirDeep: '#120e0e',
        noirWarm: '#241c1b',
        cottonShadow: '#d8d2bd',
        /* mixes (parents noted) */
        paleMaroon: mix(COTTON, MAROON, 0.28), // distant mountain mass
        fainterMaroon: mix(COTTON, MAROON, 0.14), // furthest ridge
        maroonDeep: mix(MAROON, NOIR, 0.4), // shadowed maroon
        seaBone: mix('#ddd8c6', MAROON, 0.1), // warm sea stroke
        seaLight: mix(COTTON, '#ddd8c6', 0.5), // lit water
        skin: mix('#d9a9a2', COTTON, 0.45), // the Muse's skin
        emberHot: mix(CHERRY, COTTON, 0.4), // heart of the ember
        foliage: mix('#ddd8c6', NOIR, 0.6), // olive canopy (warm grey-noir)
        foliageDark: mix('#ddd8c6', NOIR, 0.78), // cypress / shade foliage
        inkSoft: mix(NOIR, COTTON, 0.28), // softened drawing ink
        woodWarm: mix('#3a2422', COTTON, 0.22), // lit workshop timber
      };

      /* Deterministic per-element stream — same seed, same painting. */
      const rng = (seed) => {
        let n = seed * 977 + 31;
        return () => {
          n += 1;
          return hash(n);
        };
      };

      /* =============================================================
         2. STROKE KIT — the hand of the painter.
         ============================================================= */

      /* Whole-figure / whole-pass alpha multiplier: the kit functions
         each set globalAlpha, so fades (the Muse arriving, garnish
         crossing a transition) ride through this instead. */
      let ALPHA_MUL = 1;

      /* A tapered stroke along a polyline: width swells to a belly and
         thins to the lift-off, with per-segment jitter (bristle). */
      const strokePath = (g, pts, wdt, color, alpha, seed) => {
        const r = rng(seed || 1);
        g.strokeStyle = color;
        g.globalAlpha = alpha * ALPHA_MUL;
        g.lineCap = 'round';
        g.lineJoin = 'round';
        const n = pts.length - 1;
        for (let i = 0; i < n; i++) {
          const u = (i + 0.5) / n;
          const bell = Math.sin(Math.pow(u, 0.85) * Math.PI);
          g.lineWidth = Math.max(wdt * (0.18 + bell * 0.82) * (0.82 + r() * 0.36), 0.5);
          g.beginPath();
          g.moveTo(pts[i][0], pts[i][1]);
          g.lineTo(pts[i + 1][0], pts[i + 1][1]);
          g.stroke();
        }
        g.globalAlpha = 1;
      };

      /* Straight tapered stroke (the workhorse). */
      const taper = (g, x0, y0, x1, y1, wdt, color, alpha, segs, seed) => {
        const n = segs || 6;
        const pts = [];
        for (let i = 0; i <= n; i++) pts.push([lerp(x0, x1, i / n), lerp(y0, y1, i / n)]);
        strokePath(g, pts, wdt, color, alpha, seed);
      };

      /* An opaque daub — one press of the loaded brush. */
      const daub = (g, x, y, rx, ry, ang, color, alpha) => {
        g.save();
        g.translate(x, y);
        g.rotate(ang || 0);
        g.scale(1, Math.max(ry / Math.max(rx, 0.001), 0.05));
        g.globalAlpha = alpha * ALPHA_MUL;
        g.fillStyle = color;
        g.beginPath();
        g.arc(0, 0, rx, 0, Math.PI * 2);
        g.fill();
        g.restore();
        g.globalAlpha = 1;
      };

      /* A long dragged stroke (sky washes, water): wobbling centreline,
         occasionally broken — dry-brush gaps where the paint ran out. */
      const drag = (g, x, y, len, wdt, color, alpha, seed) => {
        const r = rng(seed || 3);
        const segs = 7;
        let px = x;
        let py = y;
        for (let i = 0; i < segs; i++) {
          const nx = x + (len * (i + 1)) / segs;
          const ny = y + (r() - 0.5) * wdt * 0.9;
          if (r() > 0.82) {
            px = nx;
            py = ny;
            continue; // the gap — brush skipped
          }
          const u = (i + 0.5) / segs;
          g.strokeStyle = color;
          g.globalAlpha = alpha * (0.72 + r() * 0.4) * ALPHA_MUL;
          g.lineCap = 'round';
          g.lineWidth = Math.max(wdt * (0.35 + Math.sin(u * Math.PI) * 0.65) * (0.8 + r() * 0.4), 0.5);
          g.beginPath();
          g.moveTo(px, py);
          g.lineTo(nx, ny);
          g.stroke();
          px = nx;
          py = ny;
        }
        g.globalAlpha = 1;
      };

      /* Dry scumble — broken little touches over a patch (tooth, worn
         plaster, ground texture). */
      const scumble = (g, x, y, w, h, color, alpha, count, seed) => {
        const r = rng(seed || 5);
        for (let i = 0; i < count; i++) {
          daub(
            g,
            x + r() * w,
            y + r() * h,
            1.2 + r() * 3.4,
            0.7 + r() * 1.6,
            r() * Math.PI,
            color,
            alpha * (0.5 + r() * 0.6)
          );
        }
      };

      /* A thin hand-drawn ink line — never ruler-straight. */
      const inkline = (g, x0, y0, x1, y1, wdt, alpha, seed) => {
        const r = rng(seed || 7);
        const n = 7;
        const pts = [];
        const dx = x1 - x0;
        const dy = y1 - y0;
        const L = Math.hypot(dx, dy) || 1;
        const nx = -dy / L;
        const ny = dx / L;
        for (let i = 0; i <= n; i++) {
          const u = i / n;
          const wob = (r() - 0.5) * Math.min(L * 0.02, 2.6) * (i === 0 || i === n ? 0.3 : 1);
          pts.push([x0 + dx * u + nx * wob, y0 + dy * u + ny * wob]);
        }
        strokePath(g, pts, wdt, C.noir, alpha, seed);
      };

      /* Painterly glow — stacked irregular translucent daubs, never a
         radial gradient. */
      const glow = (g, x, y, rad, color, alpha, seed) => {
        const r = rng(seed || 9);
        for (let i = 0; i < 7; i++) {
          const k = 1 - i / 7;
          daub(
            g,
            x + (r() - 0.5) * rad * 0.24,
            y + (r() - 0.5) * rad * 0.2,
            rad * (0.28 + (1 - k) * 0.8),
            rad * (0.22 + (1 - k) * 0.66),
            r() * Math.PI,
            color,
            alpha * k * 0.5
          );
        }
      };

      /* Grounded contact shadow (stacked soft ellipses, no gradient). */
      const contactShadow = (g, x, y, rx, alpha) => {
        daub(g, x, y, rx, rx * 0.24, 0, C.umber, alpha * 0.16);
        daub(g, x, y, rx * 0.66, rx * 0.17, 0, C.umber, alpha * 0.2);
        daub(g, x, y, rx * 0.36, rx * 0.1, 0, C.noirWarm, alpha * 0.22);
      };

      const smooth = (v) => {
        const t = clamp(v, 0, 1);
        return t * t * (3 - 2 * t);
      };

      /* =============================================================
         3. FIGURE KIT — Greek things, built from strokes.
         ============================================================= */

      /* A Doric column with entasis: lit cotton flank, bone shade,
         wobbled ink contours, capital and base slabs. */
      const doricColumn = (g, x, baseY, hgt, wdt, seed) => {
        const r = rng(seed);
        const top = baseY - hgt;
        for (let i = 0; i < 5; i++) {
          const u = i / 5;
          const ww = wdt * (1 - u * 0.2) * (1 + Math.sin(u * Math.PI) * 0.07);
          const y0 = baseY - hgt * u;
          const y1 = baseY - hgt * Math.min(u + 0.22, 1);
          taper(g, x - ww * 0.16, y0, x - ww * 0.16, y1, ww, C.bone, 0.95, 3, seed + i);
          taper(g, x - ww * 0.3, y0, x - ww * 0.3, y1, ww * 0.44, C.cotton, 0.8, 3, seed + i + 40);
        }
        /* flute shadows — three broken vertical drags */
        for (let f = 0; f < 3; f++) {
          const fx = x - wdt * 0.28 + f * wdt * 0.3 + (r() - 0.5) * 2;
          taper(g, fx, baseY - 6, fx + (r() - 0.5) * 3, top + 10, 1.6, C.cottonShadow, 0.5, 5, seed + 60 + f);
        }
        /* ink contours */
        inkline(g, x - wdt * 0.52, baseY, x - wdt * 0.42, top + 4, 1.6, 0.5, seed + 71);
        inkline(g, x + wdt * 0.52, baseY, x + wdt * 0.42, top + 4, 1.4, 0.34, seed + 72);
        /* capital + abacus */
        daub(g, x, top, wdt * 0.72, wdt * 0.2, 0, C.bone, 0.96);
        g.fillStyle = C.bone;
        g.globalAlpha = 0.96;
        g.fillRect(x - wdt * 0.78, top - wdt * 0.3, wdt * 1.56, wdt * 0.26);
        g.globalAlpha = 1;
        inkline(g, x - wdt * 0.78, top - wdt * 0.04, x + wdt * 0.78, top - wdt * 0.1, 1.3, 0.4, seed + 73);
        /* base slab */
        g.fillStyle = C.bone;
        g.globalAlpha = 0.92;
        g.fillRect(x - wdt * 0.8, baseY - wdt * 0.18, wdt * 1.6, wdt * 0.22);
        g.globalAlpha = 1;
      };

      /* A small far temple — a pale idea on a ridge. */
      const templeFar = (g, x, baseY, w, h, seed, tone) => {
        const col = tone || C.bone;
        const r = rng(seed);
        const cols = 5;
        g.fillStyle = col;
        g.globalAlpha = 0.85;
        g.fillRect(x - w / 2 - 3, baseY - h * 0.1, w + 6, h * 0.1);
        for (let i = 0; i < cols; i++) {
          const cx = x - w / 2 + (w / (cols - 1)) * i + (r() - 0.5) * 1.5;
          g.fillRect(cx - w * 0.028, baseY - h * 0.72, w * 0.056, h * 0.64);
        }
        g.fillRect(x - w / 2 - 2, baseY - h * 0.82, w + 4, h * 0.1);
        g.beginPath();
        g.moveTo(x - w / 2 - 4, baseY - h * 0.82);
        g.lineTo(x + (r() - 0.5) * 3, baseY - h);
        g.lineTo(x + w / 2 + 4, baseY - h * 0.82);
        g.closePath();
        g.fill();
        g.globalAlpha = 1;
      };

      /* Cypress — a dark warm flame (never green). */
      const cypress = (g, x, baseY, hgt, seed) => {
        const r = rng(seed);
        for (let i = 0; i < 13; i++) {
          const u = i / 13;
          const ww = hgt * 0.15 * Math.sin(Math.min(u * 1.3, 1) * Math.PI) + 1.6;
          taper(
            g,
            x + (r() - 0.5) * hgt * 0.05,
            baseY - u * hgt,
            x + (r() - 0.5) * hgt * 0.05,
            baseY - Math.min(u + 0.16, 1) * hgt,
            ww,
            r() > 0.45 ? C.foliageDark : C.umber,
            0.88,
            3,
            seed + i
          );
        }
        /* sun-side rim */
        taper(g, x - hgt * 0.04, baseY - hgt * 0.28, x - hgt * 0.02, baseY - hgt * 0.8, 1.8, C.bone, 0.3, 4, seed + 30);
      };

      /* Olive tree — twisted trunk, warm grey-noir daub canopy. */
      const olive = (g, x, baseY, s, seed) => {
        const r = rng(seed);
        strokePath(
          g,
          [
            [x, baseY],
            [x - 5 * s, baseY - 14 * s],
            [x + 2 * s, baseY - 26 * s],
            [x - 3 * s, baseY - 38 * s],
          ],
          6.5 * s,
          C.umber,
          0.92,
          seed
        );
        for (let c = 0; c < 3; c++) {
          const cx = x + (r() - 0.5) * 38 * s;
          const cy = baseY - (36 + r() * 20) * s;
          for (let i = 0; i < 12; i++) {
            const ang = r() * Math.PI * 2;
            const d = r() * 15 * s;
            const top = Math.sin(ang) < -0.3;
            daub(
              g,
              cx + Math.cos(ang) * d,
              cy + Math.sin(ang) * d * 0.66,
              (3.6 + r() * 4.4) * s,
              (2.2 + r() * 2.6) * s,
              r() * 0.9 - 0.45,
              top ? C.bone : C.foliage,
              top ? 0.5 : 0.8
            );
          }
        }
      };

      /* Laurel sprig — a curved stem with paired leaf daubs. */
      const laurel = (g, x, y, len, ang, s, seed) => {
        const r = rng(seed);
        const pts = [];
        for (let i = 0; i <= 6; i++) {
          const u = i / 6;
          pts.push([
            x + Math.cos(ang + u * 0.5) * len * u,
            y + Math.sin(ang + u * 0.5) * len * u,
          ]);
        }
        strokePath(g, pts, 2.6 * s, C.umber, 0.85, seed);
        for (let i = 1; i <= 6; i++) {
          const u = i / 6;
          const px = x + Math.cos(ang + u * 0.5) * len * u;
          const py = y + Math.sin(ang + u * 0.5) * len * u;
          const la = ang + u * 0.5;
          for (const side of [-1, 1]) {
            daub(
              g,
              px + Math.cos(la + side * 1.25) * 8 * s,
              py + Math.sin(la + side * 1.25) * 8 * s,
              7 * s * (0.75 + r() * 0.4),
              2.6 * s,
              la + side * 1.05,
              r() > 0.35 ? C.foliage : C.foliageDark,
              0.85
            );
          }
        }
      };

      /* A dove — two wing strokes and a body daub. flap in 0..1. */
      const dove = (g, x, y, s, flap, settled, seed) => {
        const r = rng(seed || 11);
        if (settled) {
          daub(g, x, y, 5.2 * s, 3.6 * s, 0.15, C.cotton, 0.95);
          daub(g, x + 3.4 * s, y - 2.4 * s, 2 * s, 1.8 * s, 0, C.cotton, 0.95);
          taper(g, x - 4 * s, y, x - 7.5 * s, y + 1.2 * s, 2 * s, C.cottonShadow, 0.8, 2, seed);
          inkline(g, x + 4.6 * s, y - 2.4 * s, x + 6 * s, y - 2 * s, 0.9, 0.6, seed);
          return;
        }
        const wa = Math.sin(flap * Math.PI * 2) * 0.9;
        daub(g, x, y, 4.6 * s, 2.6 * s, 0.1, C.cotton, 0.92);
        taper(g, x, y, x - 9 * s, y - (5 + wa * 6) * s, 3.4 * s, C.cotton, 0.85, 3, seed);
        taper(g, x, y, x + 8 * s, y - (5 - wa * 6) * s, 3.2 * s, C.cottonShadow, 0.8, 3, seed + 1);
        daub(g, x + 4 * s, y - 1.6 * s, 1.7 * s, 1.5 * s, 0, C.cotton, 0.95);
        taper(g, x - 4 * s, y + 0.5 * s, x - 7 * s, y + 2 * s, 1.8 * s, C.inkSoft, 0.5, 2, seed + 2);
      };

      /* A goat — noir stroke body with a cotton rim on the sun side. */
      const goat = (g, x, y, s, seed, flip) => {
        g.save();
        g.translate(x, y);
        if (flip) g.scale(-1, 1);
        taper(g, -9 * s, -9 * s, 8 * s, -10 * s, 9 * s, C.noirWarm, 0.95, 4, seed);
        daub(g, 10 * s, -14 * s, 3.4 * s, 2.8 * s, -0.3, C.noirWarm, 0.95); // head
        taper(g, 11 * s, -16 * s, 14 * s, -21 * s, 1.4, C.umber, 0.9, 2, seed + 1); // horn
        taper(g, 12.4 * s, -16 * s, 16 * s, -19.5 * s, 1.2, C.umber, 0.85, 2, seed + 2);
        for (let l = 0; l < 4; l++) {
          const lx = -7 * s + l * 4.6 * s;
          taper(g, lx, -6 * s, lx + (l % 2 ? 1.4 : -1.2) * s, 0, 1.8 * s, C.noirDeep, 0.9, 2, seed + 3 + l);
        }
        taper(g, -9 * s, -12 * s, 7 * s, -13 * s, 1.6, C.bone, 0.55, 3, seed + 8); // rim light
        g.restore();
      };

      /* A painted picture frame with a vague canvas inside. */
      const pictureFrame = (g, x, y, w, h, seed, tone) => {
        const r = rng(seed);
        g.fillStyle = C.parchment;
        g.globalAlpha = 0.95;
        g.fillRect(x, y, w, h);
        g.globalAlpha = 1;
        /* the vague painting inside — soft daubs, no subject */
        for (let i = 0; i < 7; i++) {
          daub(
            g,
            x + w * (0.16 + r() * 0.68),
            y + h * (0.2 + r() * 0.6),
            w * (0.06 + r() * 0.12),
            h * (0.05 + r() * 0.1),
            r() * Math.PI,
            i % 3 === 0 ? C.paleMaroon : i % 3 === 1 ? C.bone : C.rose,
            0.4 + r() * 0.3
          );
        }
        /* frame strokes — four hand runs, slightly out of true */
        const fr = tone || C.umber;
        taper(g, x - 2, y + (r() - 0.5) * 2, x + w + 2, y + (r() - 0.5) * 2, 5, fr, 0.9, 5, seed + 1);
        taper(g, x - 2, y + h + (r() - 0.5) * 2, x + w + 2, y + h + (r() - 0.5) * 2, 5, fr, 0.9, 5, seed + 2);
        taper(g, x + (r() - 0.5) * 2, y - 2, x + (r() - 0.5) * 2, y + h + 2, 5, fr, 0.9, 5, seed + 3);
        taper(g, x + w + (r() - 0.5) * 2, y - 2, x + w + (r() - 0.5) * 2, y + h + 2, 5, fr, 0.9, 5, seed + 4);
        inkline(g, x + 2, y + 3, x + w - 2, y + 3, 1, 0.3, seed + 5);
      };

      /* Sky of dragged washes over flat cotton (no gradients). */
      const paintSky = (g, w, hgt, horizon, seed, roseAmt) => {
        const r = rng(seed);
        g.fillStyle = C.cotton;
        g.fillRect(0, 0, w, hgt);
        const bands = 26;
        for (let i = 0; i < bands; i++) {
          const u = i / bands;
          const y = u * horizon * 1.04;
          const tone =
            r() < roseAmt * (0.3 + u * 0.7) ? C.rose : r() > 0.55 ? C.bone : C.cottonShadow;
          drag(
            g,
            -40 + r() * w * 0.5,
            y + (r() - 0.5) * 8,
            w * (0.4 + r() * 0.7),
            5 + r() * 13 + u * 8,
            tone,
            0.1 + u * 0.12 + r() * 0.06,
            seed + i * 3
          );
        }
        /* near the horizon the washes warm up */
        for (let i = 0; i < 6; i++) {
          drag(
            g,
            r() * w * 0.6 - 30,
            horizon - 12 - r() * 26,
            w * (0.5 + r() * 0.5),
            8 + r() * 10,
            r() > 0.5 ? C.rose : C.paleMaroon,
            0.08 + r() * 0.07,
            seed + 90 + i
          );
        }
      };

      /* A water band of broken horizontal strokes. */
      const paintSea = (g, w, y0, y1, seed, lightX) => {
        const r = rng(seed);
        g.fillStyle = C.seaLight;
        g.fillRect(0, y0, w, y1 - y0);
        const rows = Math.max(6, Math.round((y1 - y0) / 9));
        for (let i = 0; i < rows; i++) {
          const u = i / rows;
          const y = y0 + u * (y1 - y0);
          const n = 3 + Math.round(r() * 3);
          for (let k = 0; k < n; k++) {
            drag(
              g,
              r() * w - 30,
              y + (r() - 0.5) * 4,
              w * (0.1 + r() * 0.24) * (0.5 + u),
              2 + u * 5 + r() * 3,
              r() > 0.72 ? C.cotton : r() > 0.36 ? C.bone : C.seaBone,
              0.16 + u * 0.18,
              seed + i * 17 + k
            );
          }
        }
        /* pale glitter path under the light, if any */
        if (lightX >= 0) {
          for (let i = 0; i < 14; i++) {
            const u = r();
            drag(
              g,
              lightX - 30 + (r() - 0.5) * 70 * (0.4 + u),
              y0 + u * (y1 - y0) * 0.8,
              16 + r() * 30,
              1.6 + r() * 2,
              C.cotton,
              0.3,
              seed + 200 + i
            );
          }
        }
      };

      /* =============================================================
         4. THE MUSE — a stroke-built figure out of a Waterhouse canvas.
         Cotton chiton with bone folds, noir hair sweeps, rose-bone
         skin, ONE cherry hair-ribbon. Walk driven by scroll distance;
         idles ~700ms after the visitor stops; per-scene choreography;
         never covers a text panel.
         ============================================================= */
      const muse = {
        x: -400,
        y: 0,
        scale: 1,
        facing: 1,
        faceWish: 1,
        phase: 0,
        moving: false,
        lastMoveAt: -1e9,
        headTurn: 0,
        headTarget: 0,
        nextTurnAt: 3,
        hem: 0,
        alpha: 0,
        visible: false,
      };

      /* pose = { gesture 0..1, gestureDir -1|1, bow 0..1, observe 0..1 } */
      const drawMuse = (g, m, pose, gt, idle) => {
        const s = m.scale;
        if (m.alpha <= 0.02 || s <= 0.05) return;
        g.save();
        ALPHA_MUL = m.alpha;
        g.translate(m.x, m.y);

        contactShadow(g, 0, 1.5 * s, 30 * s, 1);
        g.scale(m.facing, 1);

        const walkAmt = m.moving ? 1 : 0;
        const swing = Math.sin(m.phase) * walkAmt;
        const breath = idle ? Math.sin(gt * 1.4) * 0.012 : 0;
        const hemSway = m.hem + swing * 5 * s;

        /* -- legs beneath the hem (visible only mid-stride) */
        const skinT = C.skin;
        taper(g, -3 * s, -34 * s, -3 * s - swing * 11 * s, 0, 5.5 * s, skinT, 0.9, 3, 101);
        taper(g, 4 * s, -34 * s, 4 * s + swing * 11 * s, 0, 5.5 * s, mix(C.skin, C.umber, 0.25), 0.85, 3, 102);
        daub(g, -3 * s - swing * 11 * s, 0.5 * s, 3.4 * s, 1.5 * s, 0, C.rose, 0.7);
        daub(g, 4 * s + swing * 11 * s, 0.5 * s, 3.2 * s, 1.4 * s, 0, C.rose, 0.6);

        /* -- torso group (bows from the hip) */
        g.save();
        g.translate(0, -52 * s);
        if (pose.bow > 0) g.rotate(pose.bow * 0.62);
        g.scale(1 + breath * 0.4, 1 + breath);

        /* chiton — layered falls of cotton with bone shadow folds */
        strokePath(
          g,
          [
            [0, -56 * s],
            [-2 * s, -30 * s],
            [-4 * s - hemSway * 0.3, -4 * s],
            [-5 * s - hemSway, 22 * s],
          ],
          30 * s,
          C.cotton,
          0.98,
          111
        );
        strokePath(
          g,
          [
            [3 * s, -52 * s],
            [6 * s, -24 * s],
            [8 * s + hemSway * 0.6, 20 * s],
          ],
          16 * s,
          C.parchment,
          0.9,
          112
        );
        /* shadow folds — the drawing inside the drape */
        strokePath(
          g,
          [
            [-6 * s, -40 * s],
            [-8 * s, -12 * s],
            [-10 * s - hemSway, 18 * s],
          ],
          5 * s,
          C.bone,
          0.85,
          113
        );
        strokePath(
          g,
          [
            [1 * s, -34 * s],
            [0, -6 * s],
            [-1 * s - hemSway * 0.5, 16 * s],
          ],
          3.4 * s,
          C.cottonShadow,
          0.8,
          114
        );
        taper(g, -1 * s, -50 * s, 9 * s, -46 * s, 3 * s, C.bone, 0.6, 3, 115); // shoulder fold
        /* girdle at the waist */
        taper(g, -8 * s, -26 * s, 9 * s, -28 * s, 2.4 * s, C.cottonShadow, 0.9, 3, 116);

        /* -- far arm (behind the drape) */
        const farAx = pose.observe > 0.5 ? -10 : -8;
        strokePath(
          g,
          [
            [-6 * s, -46 * s],
            [farAx * s, -30 * s],
            [(farAx + 2) * s, -18 * s],
          ],
          4.6 * s,
          C.cottonShadow,
          0.75,
          117
        );

        /* -- head above the torso */
        g.save();
        g.translate(m.headTurn * 4 * s, -62 * s + pose.bow * 10 * s);
        if (pose.bow > 0) g.rotate(pose.bow * 0.3);
        daub(g, 0, 0, 6.6 * s, 7.6 * s, -0.06, skinT, 0.98); // face
        daub(g, -2.2 * s, 2.6 * s, 3 * s, 2 * s, 0.2, C.rose, 0.3); // cheek warmth
        daub(g, 1.5 * s, 6.4 * s, 3.6 * s, 1.6 * s, 0.1, mix(C.skin, C.umber, 0.3), 0.4); // jaw shade
        /* noir hair — three sweeps gathered to a low knot, trailing
           further when she walks */
        const trail = (walkAmt * 6 + 2) * s;
        strokePath(
          g,
          [
            [2 * s, -7 * s],
            [-4 * s, -6 * s],
            [-8 * s - trail * 0.4, -1 * s],
            [-9 * s - trail, 4 * s],
          ],
          7 * s,
          C.noir,
          0.96,
          121
        );
        strokePath(
          g,
          [
            [4 * s, -5 * s],
            [-2 * s, -8 * s],
            [-9 * s - trail * 0.6, -4 * s],
          ],
          4.6 * s,
          C.noirWarm,
          0.9,
          122
        );
        strokePath(
          g,
          [
            [-6 * s, 0],
            [-10 * s - trail, 6 * s],
            [-11 * s - trail * 1.3, 10 * s],
          ],
          3 * s,
          C.noir,
          0.85,
          123
        );
        daub(g, -8.5 * s, 3.5 * s, 3.8 * s, 3.2 * s, 0.3, C.noir, 0.95); // the knot
        /* THE cherry ribbon — her one jewel */
        daub(g, -8.5 * s, 1.6 * s, 2 * s, 1.2 * s, 0.5, C.cherry, 0.95);
        taper(g, -9 * s, 2.4 * s, -12 * s - trail * 0.8, 8 * s, 1.6 * s, C.cherry, 0.85, 3, 124);
        g.restore();

        /* -- near arm: relaxed, observing (clasped), or the gesture */
        const ges = pose.gesture;
        if (ges > 0.02) {
          /* unfurls from the shoulder toward gestureDir */
          const dir = pose.gestureDir * m.facing; // in local (flipped) space
          const ex = dir * (14 + 34 * ges) * s;
          const ey = -34 * s - 18 * ges * s;
          strokePath(
            g,
            [
              [4 * s, -44 * s],
              [4 * s + ex * 0.45, -38 * s + (ey + 38 * s) * 0.4],
              [4 * s + ex, ey],
            ],
            5.4 * s,
            skinT,
            0.95,
            125
          );
          daub(g, 4 * s + ex, ey, 2.6 * s, 2 * s, 0.3, skinT, 0.95); // hand
          /* sleeve fall from the raised arm */
          strokePath(
            g,
            [
              [4 * s + ex * 0.4, -38 * s],
              [3 * s + ex * 0.3, -20 * s],
            ],
            7 * s,
            C.cotton,
            0.8,
            126
          );
        } else if (pose.observe > 0.4) {
          /* hands gathered before her */
          strokePath(
            g,
            [
              [5 * s, -44 * s],
              [7 * s, -32 * s],
              [2 * s, -26 * s],
            ],
            5 * s,
            skinT,
            0.92,
            127
          );
          daub(g, 1 * s, -26 * s, 3 * s, 2.2 * s, 0.4, skinT, 0.9);
        } else {
          /* at rest along the drape */
          strokePath(
            g,
            [
              [5 * s, -44 * s],
              [8 * s, -28 * s],
              [7 * s, -14 * s],
            ],
            5 * s,
            skinT,
            0.92,
            128
          );
          daub(g, 7 * s, -13 * s, 2.6 * s, 2 * s, 0.2, skinT, 0.9);
        }

        g.restore(); // torso
        g.restore(); // muse
        ALPHA_MUL = 1;
        g.globalAlpha = 1;
      };

      /* Advance the Muse toward a desired mark. Movement is budgeted by
         scroll distance — she walks when you scroll, settles when you
         stop, and turns around when you scroll back up. */
      const updateMuse = (m, want, env, dt) => {
        if (!want || !want.visible || !want.ok) {
          m.visible = false;
          m.alpha = env.snap ? 0 : Math.max(0, m.alpha - dt * 3);
          return;
        }
        m.visible = true;
        m.scale = want.scale;
        m.y = want.y;
        if (env.snap) {
          /* deterministic placement (reduced tier / debug stills) */
          m.x = want.x;
          m.alpha = 1;
          m.phase = want.x * 0.045;
          m.moving = false;
          m.headTurn = 0;
          m.hem = 0;
          m.facing = want.face || 1;
          return;
        }
        m.alpha = Math.min(1, m.alpha + dt * 2.2);
        const budget = Math.abs(env.scrollDelta) * 1.6 + (env.sinceScroll < 0.7 ? 170 * dt : 0);
        const dx = want.x - m.x;
        if (Math.abs(dx) > 900) m.x = want.x - Math.sign(dx) * 500; // teleport-catchup after jumps
        const step = clamp(dx, -budget, budget);
        m.x += step;
        if (Math.abs(step) > 0.35) {
          m.phase += Math.abs(step) * 0.052;
          m.faceWish = step > 0 ? 1 : -1;
          m.lastMoveAt = env.gt;
          m.moving = true;
          m.hem = clamp(m.hem + step * 0.06, -6, 6);
        } else {
          m.moving = false;
          m.hem *= Math.max(0, 1 - dt * 3); // cloth settles
          if (want.face) m.faceWish = want.face;
        }
        m.facing += (m.faceWish - m.facing) * Math.min(dt * 7, 1);
        if (Math.abs(m.facing) < 0.25) m.facing = m.faceWish * 0.25; // never flat

        const idle = env.gt - m.lastMoveAt > 0.7;
        if (idle) {
          /* occasional slow head turn */
          if (env.gt > m.nextTurnAt) {
            m.headTarget = (hash(Math.floor(env.gt) * 7.7) - 0.5) * 1.1;
            m.nextTurnAt = env.gt + 4 + hash(Math.floor(env.gt) * 3.1) * 5;
          }
          /* desktop garnish: she glances toward a nearby pointer */
          if (env.pointerOk) {
            const pdx = env.px - m.x;
            const pdy = env.py - (m.y - 110 * m.scale);
            if (pdx * pdx + pdy * pdy < 200 * 200) {
              m.headTarget = clamp((pdx / 200) * m.facing, -1, 1) * 0.8;
            }
          }
        } else {
          m.headTarget = 0;
          m.nextTurnAt = env.gt + 2.5;
        }
        m.headTurn += (m.headTarget - m.headTurn) * Math.min(dt * 2.2, 1);
      };

      /* =============================================================
         5. THE SIX SCENES.
         Each painter: { far, mid, near } layer functions painted once
         per resize (g, w, h, env — env.oy anchors verticals to the
         mid-scroll window), an optional reveal() for progressive
         assembly, a dynamic() pass per frame, and muse() choreography.
         Value ladder everywhere: cotton dominant, noir ink, maroon
         masses, ONE cherry event per scene.
         ============================================================= */

      /* --- helpers shared by dynamics --- */
      const brushHead = (g, x, y, ang, color, alpha, seed) => {
        const r = rng(seed);
        for (let i = 0; i < 5; i++) {
          const off = (i - 2) * 4;
          taper(
            g,
            x + Math.cos(ang + 1.57) * off,
            y + Math.sin(ang + 1.57) * off,
            x - Math.cos(ang) * (18 + r() * 14) + Math.cos(ang + 1.57) * off,
            y - Math.sin(ang) * (18 + r() * 14) + Math.sin(ang + 1.57) * off,
            5 + r() * 4,
            color,
            alpha * (0.5 + r() * 0.5),
            3,
            seed + i
          );
        }
        daub(g, x, y, 7, 5, ang, color, alpha);
        /* flung droplets */
        daub(g, x + Math.cos(ang) * 26, y + Math.sin(ang) * 22, 2.2, 1.6, ang, color, alpha * 0.6);
        daub(g, x + Math.cos(ang + 0.5) * 38, y + Math.sin(ang + 0.5) * 30, 1.4, 1.1, 0, color, alpha * 0.4);
      };

      /* Scene 1 assembly stages (shared by reveal + dynamic). */
      const canvasStages = (t) => [
        { q: smooth((t - 0.05) / 0.3), mode: 'x' }, // horizon wash + Olympus
        { q: smooth((t - 0.34) / 0.28), mode: 'up' }, // the columns rise
        { q: smooth((t - 0.6) / 0.24), mode: 'diag' }, // laurel + margins
      ];

      const SCENES = {};

      /* ---------------- SCENE: canvas — The Blank Canvas ------------ */
      SCENES.canvas = {
        /* The physical linen itself — the whole first impression at
           t=0 and the ground beneath every stroke after: visible
           tooth and weave, warm gesso patches, faint charcoal
           underdrawing ticks of the composition to come, and the
           painter's brush and palette resting at the lower-right
           edge. Quiet — nothing competes with the text panel. */
        base(g, w, h, env) {
          const r = rng(env.seed + 2001);
          g.fillStyle = C.cotton;
          g.fillRect(0, 0, w, h);
          /* linen weave — the weft... */
          for (let y = 0; y < h; y += 12 + r() * 7) {
            drag(g, -24, y, w + 48, 2.2, C.cottonShadow, 0.16, env.seed + 2010 + y);
          }
          /* ...and the crossing warp threads */
          for (let x = 0; x < w; x += 15 + r() * 10) {
            taper(g, x + (r() - 0.5) * 3, -8, x + (r() - 0.5) * 6, h + 8, 1.4, C.bone, 0.12, 7, env.seed + 2100 + x);
          }
          /* stray raised threads catching the light */
          for (let i = 0; i < 24; i++) {
            const tx = r() * w;
            const ty = r() * h;
            taper(g, tx, ty, tx + (r() - 0.5) * 30, ty + (r() - 0.5) * 8, 1.2, r() > 0.5 ? C.parchment : C.cottonShadow, 0.25, 3, env.seed + 2200 + i);
          }
          scumble(g, 0, 0, w, h, C.parchment, 0.2, 110, env.seed + 2005);
          /* three warm gesso patches, scraped on and left to dry */
          const gesso = mix('#f4f2e8', '#d9a9a2', 0.22);
          const patches = [
            [0.57, 0.3, 0.24, 0.18],
            [0.74, 0.64, 0.15, 0.12],
            [0.3, 0.87, 0.14, 0.09],
          ];
          patches.forEach((p, pi) => {
            const pw = p[2] * w;
            const ph = p[3] * h;
            const px = p[0] * w - pw / 2;
            const py = p[1] * h - ph / 2;
            for (let i = 0; i < 12; i++) {
              drag(g, px + r() * pw * 0.3 - pw * 0.06, py + (i / 12) * ph, pw * (0.55 + r() * 0.5), 7 + r() * 9, pi === 1 ? C.parchment : gesso, 0.38, env.seed + 2300 + pi * 24 + i);
            }
            scumble(g, px, py, pw, ph, C.parchment, 0.46, 26, env.seed + 2390 + pi);
          });
          /* charcoal underdrawing — broken ticks laying in the
             horizon line where the wash will land */
          const hor = h * 0.58;
          for (let i = 0; i < 8; i++) {
            const u = i / 8;
            const x0 = w * (0.07 + u * 0.84) + (r() - 0.5) * 12;
            inkline(g, x0, hor + (r() - 0.5) * 5, x0 + w * (0.05 + r() * 0.03), hor + (r() - 0.5) * 5, 1.4, 0.2 + r() * 0.08, env.seed + 2400 + i);
          }
          /* ...the two flanks of Olympus, gestured in */
          for (let f = 0; f < 2; f++) {
            const ax = f === 0 ? 0.42 : 0.58;
            const bx2 = f === 0 ? 0.58 : 0.74;
            const ay = f === 0 ? hor : h * 0.3;
            const by3 = f === 0 ? h * 0.3 : hor;
            for (let i = 0; i < 3; i++) {
              const u0 = 0.1 + i * 0.28 + r() * 0.08;
              const u1 = u0 + 0.16;
              inkline(g, w * lerp(ax, bx2, u0), lerp(ay, by3, u0), w * lerp(ax, bx2, u1), lerp(ay, by3, u1), 1.3, 0.18, env.seed + 2420 + f * 3 + i);
            }
          }
          /* ...and the column gesture lines at the frame edges */
          for (const cxn of [0.075, 0.925]) {
            const cx2 = w * cxn;
            for (let i = 0; i < 3; i++) {
              const y0 = h * (0.2 + i * 0.26) + r() * h * 0.04;
              inkline(g, cx2 + (r() - 0.5) * 6, y0, cx2 + (r() - 0.5) * 6, y0 + h * 0.16, 1.5, 0.16, env.seed + 2440 + Math.round(cxn * 100) + i);
            }
            inkline(g, cx2 - w * 0.018, h * 0.1, cx2 + w * 0.018, h * 0.096, 1.3, 0.18, env.seed + 2452 + Math.round(cxn * 100));
          }
          /* one soft smudge where the drawing hand rested */
          drag(g, w * 0.6, hor + 14, w * 0.08, 9, C.inkSoft, 0.08, env.seed + 2460);
          /* the painter's tools waiting at the lower-right edge */
          const ps = clamp(Math.min(w, h) / 900, 0.7, 1.2);
          const palX = w * 0.905;
          const palY = h * 0.905;
          contactShadow(g, palX, palY + 10 * ps, 44 * ps, 0.8);
          /* wooden palette — an irregular oval with a thumb hole */
          daub(g, palX, palY, 46 * ps, 27 * ps, -0.18, C.woodWarm, 0.95);
          daub(g, palX - 8 * ps, palY + 3 * ps, 38 * ps, 21 * ps, -0.14, mix('#3a2422', '#edebdd', 0.34), 0.75);
          inkline(g, palX - 44 * ps, palY + 8 * ps, palX + 40 * ps, palY - 12 * ps, 1.2, 0.25, env.seed + 2470);
          daub(g, palX + 26 * ps, palY - 9 * ps, 6.5 * ps, 4.5 * ps, 0.2, C.cotton, 0.9);
          /* dried pigment wells along its rim (no cherry — the seal
             owns this scene's cherry) */
          daub(g, palX - 26 * ps, palY - 10 * ps, 4.5 * ps, 3.2 * ps, 0.3, C.maroon, 0.85);
          daub(g, palX - 14 * ps, palY - 14 * ps, 4 * ps, 3 * ps, -0.2, C.noirWarm, 0.8);
          daub(g, palX - 2 * ps, palY - 15 * ps, 4 * ps, 3 * ps, 0.1, C.rose, 0.8);
          daub(g, palX + 10 * ps, palY - 13 * ps, 4 * ps, 3 * ps, 0.4, C.bone, 0.85);
          /* the brush at rest beside it, bristles still stained */
          const bx0 = w * 0.79;
          const by0 = h * 0.945;
          const bx1 = bx0 + 62 * ps;
          const by1 = by0 - 26 * ps;
          contactShadow(g, (bx0 + bx1) / 2, by0 - 2 * ps, 30 * ps, 0.5);
          taper(g, bx0, by0, bx1, by1, 4.2 * ps, C.woodWarm, 0.95, 5, env.seed + 2480);
          taper(g, bx1, by1, bx1 + 9 * ps, by1 - 4 * ps, 5 * ps, C.noirWarm, 0.9, 2, env.seed + 2481);
          taper(g, bx1 + 9 * ps, by1 - 4 * ps, bx1 + 22 * ps, by1 - 9 * ps, 4 * ps, C.umber, 0.9, 3, env.seed + 2482);
          daub(g, bx1 + 21 * ps, by1 - 9 * ps, 3 * ps, 2 * ps, -0.4, C.maroon, 0.8);
          /* the small dried maroon daub where the brush was set down */
          daub(g, bx0 - 14 * ps, by0 - 6 * ps, 5 * ps, 3.4 * ps, 0.5, C.maroon, 0.8);
          daub(g, bx0 - 12 * ps, by0 - 7 * ps, 2.4 * ps, 1.6 * ps, 0.3, mix('#630000', '#edebdd', 0.3), 0.7);
        },
        far(g, w, h, env) {
          const r = rng(env.seed + 1);
          /* (the raw linen ground lives in base — the far layer is
             only the paint laid onto it, so the weave breathes
             through the washes) */
          const hor = env.oy + env.H * 0.58;
          /* horizon washes — rose and bone pulled flat */
          for (let i = 0; i < 12; i++) {
            drag(
              g,
              -30 + r() * w * 0.4,
              hor - 26 + r() * 44,
              w * (0.45 + r() * 0.6),
              7 + r() * 12,
              r() > 0.6 ? C.rose : r() > 0.3 ? C.bone : C.paleMaroon,
              0.14 + r() * 0.1,
              env.seed + 20 + i
            );
          }
          /* Mount Olympus — a pale maroon mass with a lit crown */
          const peakX = w * 0.58;
          const peakY = env.oy + env.H * 0.28;
          g.fillStyle = C.paleMaroon;
          g.globalAlpha = 0.85;
          g.beginPath();
          g.moveTo(w * 0.34, hor + 6);
          let px = w * 0.34;
          for (let i = 1; i <= 10; i++) {
            const u = i / 10;
            const x = lerp(w * 0.34, w * 0.86, u);
            const ridge =
              u < 0.5 ? lerp(hor, peakY, smooth(u / 0.5)) : lerp(peakY, hor + 2, smooth((u - 0.5) / 0.5));
            g.lineTo(x, ridge + (hash(env.seed + i * 3.7) - 0.5) * env.H * 0.03);
            px = x;
          }
          g.lineTo(px, hor + 6);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          /* second, fainter ridge behind */
          g.fillStyle = C.fainterMaroon;
          g.globalAlpha = 0.7;
          g.beginPath();
          g.moveTo(w * 0.05, hor + 4);
          for (let i = 0; i <= 8; i++) {
            const x = lerp(w * 0.05, w * 0.5, i / 8);
            g.lineTo(x, hor - env.H * (0.05 + 0.05 * Math.sin(i * 1.9 + env.seed)) );
          }
          g.lineTo(w * 0.5, hor + 4);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          /* modelling strokes on the mountain + snow-light crown */
          for (let i = 0; i < 14; i++) {
            const u = r();
            taper(
              g,
              lerp(w * 0.4, w * 0.8, u),
              lerp(hor, peakY + 20, r() * 0.9),
              lerp(w * 0.4, w * 0.8, u) + 30 + r() * 40,
              lerp(hor, peakY + 30, r() * 0.9) + 10,
              4 + r() * 7,
              r() > 0.5 ? C.paleMaroon : mix(COTTON, MAROON, 0.4),
              0.3,
              4,
              env.seed + 40 + i
            );
          }
          taper(g, peakX - w * 0.05, peakY + env.H * 0.02, peakX + w * 0.03, peakY - 4, 5, C.parchment, 0.85, 4, env.seed + 60);
          taper(g, peakX - w * 0.01, peakY + env.H * 0.045, peakX + w * 0.06, peakY + env.H * 0.02, 3.4, C.bone, 0.7, 3, env.seed + 61);
        },
        mid(g, w, h, env) {
          /* two Doric columns framing the composition edges */
          const baseY = env.oy + env.H * 0.97;
          doricColumn(g, w * 0.075, baseY, env.H * 0.9, Math.max(w * 0.052, 40), env.seed + 80);
          doricColumn(g, w * 0.925, baseY, env.H * 0.9, Math.max(w * 0.052, 40), env.seed + 140);
          contactShadow(g, w * 0.075, baseY, w * 0.05, 0.8);
          contactShadow(g, w * 0.925, baseY, w * 0.05, 0.8);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 9);
          /* laurel sprig signed into the lower corner */
          laurel(g, w * 0.13, env.oy + env.H * 0.9, w * 0.1, -0.5, 1, env.seed + 11);
          /* unpainted margin — ragged cotton-shadow border strokes */
          for (let i = 0; i < 26; i++) {
            const edge = i % 4;
            const u = r();
            const wdt = 6 + r() * 14;
            if (edge === 0) drag(g, u * w - 20, 4 + r() * 8, 60 + r() * 90, wdt, C.cottonShadow, 0.3, env.seed + 200 + i);
            else if (edge === 1) drag(g, u * w - 20, h - 10 - r() * 8, 60 + r() * 90, wdt, C.cottonShadow, 0.32, env.seed + 200 + i);
            else if (edge === 2) taper(g, 5 + r() * 8, u * h, 6 + r() * 10, u * h + 60 + r() * 60, wdt, C.cottonShadow, 0.3, 4, env.seed + 200 + i);
            else taper(g, w - 6 - r() * 8, u * h, w - 7 - r() * 10, u * h + 60 + r() * 60, wdt, C.cottonShadow, 0.3, 4, env.seed + 200 + i);
          }
          /* two honest drips where the wash ran */
          taper(g, w * 0.36, env.oy + env.H * 0.62, w * 0.36 + 2, env.oy + env.H * 0.72, 2.2, C.paleMaroon, 0.4, 4, env.seed + 13);
          daub(g, w * 0.36 + 2, env.oy + env.H * 0.725, 2.2, 3, 0, C.paleMaroon, 0.45);
        },
        reveal(layer, t) {
          const st = canvasStages(t);
          if (layer === 'far') return st[0];
          if (layer === 'mid') return st[1];
          return st[2];
        },
        dynamic(g, env) {
          const st = canvasStages(env.reduced ? 1 : env.t);
          /* the working brush at the leading edge of the wet stage */
          if (!env.reduced) {
            for (let i = 0; i < st.length; i++) {
              const q = st[i].q;
              if (q <= 0.02 || q >= 0.98) continue;
              if (st[i].mode === 'x') {
                const bx = q * env.W * 1.12 - 40;
                const by = env.H * (0.44 + Math.sin(q * 9) * 0.1);
                brushHead(g, bx, by, 0.15, q > 0.6 ? C.rose : C.bone, 0.85, 301);
              } else if (st[i].mode === 'up') {
                const by = env.H * (1 - q * 1.02);
                brushHead(g, env.W * 0.075 + 14, by, -1.4, C.bone, 0.8, 302);
                brushHead(g, env.W * 0.925 - 14, by + env.H * 0.05, -1.7, C.cottonShadow, 0.75, 303);
              } else {
                const d = q * (env.W + env.H);
                const bx = clamp(d - env.H * 0.45, env.W * 0.08, env.W * 0.92);
                const by = clamp(d - bx, 20, env.H - 20);
                brushHead(g, bx, by, 0.7, C.foliage, 0.7, 304);
              }
              break;
            }
          }
          /* CHERRY EVENT: the painter's seal, pressed at completion */
          const sq = env.reduced ? 1 : smooth((env.t - 0.86) / 0.07);
          if (sq > 0.02) {
            const sx = env.W * 0.895;
            const sy = env.H * 0.865;
            const press = 1.5 - 0.5 * sq;
            g.save();
            g.globalAlpha = sq;
            daub(g, sx, sy, 12 * press, 11 * press, 0.2, C.cherry, 0.95);
            daub(g, sx - 3, sy - 3, 9 * press, 8 * press, 0.5, C.cherry, 0.9);
            daub(g, sx + 4, sy + 3, 7 * press, 7 * press, -0.3, C.maroon, 0.5);
            /* the monogram cut — unpainted cotton showing through */
            taper(g, sx - 5, sy + 4, sx + 1, sy - 5, 2.2, C.cotton, 0.9, 3, 305);
            taper(g, sx + 1, sy - 5, sx + 5, sy + 4, 2, C.cotton, 0.85, 3, 306);
            g.restore();
          }
        },
        muse: null,
      };

      /* ---------------- SCENE: muse — The Muse Appears --------------- */
      SCENES.muse = {
        far(g, w, h, env) {
          paintSky(g, w, h, env.oy + env.H * 0.66, env.seed + 400, 0.55);
          /* furthest ridge with a memory of a temple */
          const ry = env.oy + env.H * 0.64;
          g.fillStyle = C.fainterMaroon;
          g.globalAlpha = 0.85;
          g.beginPath();
          g.moveTo(-4, ry + env.H * 0.04);
          for (let i = 0; i <= 12; i++) {
            const x = (w * i) / 12;
            g.lineTo(x, ry - env.H * 0.05 * Math.abs(Math.sin(i * 1.3 + env.seed * 0.1)));
          }
          g.lineTo(w + 4, h);
          g.lineTo(-4, h);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          templeFar(g, w * 0.24, ry - env.H * 0.028, w * 0.07, env.H * 0.05, env.seed + 402, C.paleMaroon);
          /* haze where ridge meets sky */
          for (let i = 0; i < 8; i++) {
            drag(g, hash(env.seed + i * 9.1) * w - 40, ry - 8 + i * 3, w * 0.5, 8, C.cotton, 0.16, env.seed + 410 + i);
          }
        },
        mid(g, w, h, env) {
          const r = rng(env.seed + 420);
          const platY = env.oy + env.H * 0.8;
          /* colonnade on a bone stylobate */
          g.fillStyle = C.bone;
          g.globalAlpha = 0.9;
          g.fillRect(w * 0.06, platY, w * 0.88, env.H * 0.03);
          g.globalAlpha = 1;
          inkline(g, w * 0.06, platY + 2, w * 0.94, platY, 1.6, 0.4, env.seed + 421);
          const colH = env.H * 0.34;
          for (let i = 0; i < 5; i++) {
            const cx = w * (0.14 + i * 0.18);
            doricColumn(g, cx, platY, colH, Math.max(w * 0.022, 16), env.seed + 430 + i * 7);
          }
          /* architrave */
          g.fillStyle = C.bone;
          g.globalAlpha = 0.92;
          g.fillRect(w * 0.09, platY - colH - env.H * 0.045, w * 0.82, env.H * 0.03);
          g.globalAlpha = 1;
          inkline(g, w * 0.09, platY - colH - env.H * 0.045, w * 0.91, platY - colH - env.H * 0.048, 1.4, 0.45, env.seed + 436);
          inkline(g, w * 0.09, platY - colH - env.H * 0.015, w * 0.91, platY - colH - env.H * 0.018, 1.2, 0.3, env.seed + 437);
          /* cypresses breathing behind the stone */
          cypress(g, w * 0.3, platY + 4, env.H * 0.3, env.seed + 440);
          cypress(g, w * 0.66, platY + 4, env.H * 0.36, env.seed + 441);
          cypress(g, w * 0.85, platY + 2, env.H * 0.26, env.seed + 442);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 450);
          const gy = env.oy + env.H * 0.84;
          /* parchment ground */
          g.fillStyle = C.parchment;
          g.beginPath();
          g.moveTo(-4, gy + env.H * 0.02);
          for (let i = 0; i <= 10; i++) g.lineTo((w * i) / 10, gy + Math.sin(i * 2.1 + env.seed) * 5);
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          for (let i = 0; i < 30; i++) {
            drag(
              g,
              r() * w - 20,
              gy + 10 + r() * (h - gy - 12),
              40 + r() * 120,
              4 + r() * 8,
              r() > 0.62 ? C.bone : r() > 0.3 ? C.cottonShadow : mix(COTTON, MAROON, 0.12),
              0.25,
              env.seed + 452 + i
            );
          }
          /* worn paving slabs */
          for (let i = 0; i < 5; i++) {
            const sx = w * (0.08 + i * 0.2) + (r() - 0.5) * 30;
            const sy = gy + env.H * (0.05 + r() * 0.06);
            daub(g, sx, sy, 34 + r() * 18, 8 + r() * 4, (r() - 0.5) * 0.2, C.bone, 0.6);
            inkline(g, sx - 30, sy + 6, sx + 26, sy + 8, 1, 0.25, env.seed + 470 + i);
          }
          /* grass tufts in the joints */
          for (let i = 0; i < 16; i++) {
            const x = r() * w;
            const y = gy + 8 + r() * (h - gy - 10);
            for (let b = 0; b < 3; b++) {
              taper(g, x, y, x + (r() - 0.5) * 10, y - 6 - r() * 9, 1.4, r() > 0.5 ? C.foliage : C.umber, 0.6, 2, env.seed + 480 + i * 3 + b);
            }
          }
          /* repoussoir cypress at the frame edge */
          cypress(g, w * 0.985, env.oy + env.H * 1.02, env.H * 0.6, env.seed + 490);
        },
        dynamic(g, env) {
          /* two far birds — slow ink accents riding the warm air */
          if (env.calmAmt < 1) {
            const fade = 1 - env.calmAmt * 0.7;
            for (let i = 0; i < 2; i++) {
              const u = (env.reduced ? 0.4 : (env.gt * 0.014 + i * 0.37)) % 1;
              const bx = env.W * (0.15 + 0.7 * u);
              const by = env.H * (0.2 + 0.08 * Math.sin(u * 9 + i * 3));
              const fl = env.reduced ? 0.2 : (env.gt * 1.6 + i) % 1;
              const sweep = Math.sin(fl * Math.PI * 2) * 3;
              taper(g, bx - 5, by - sweep, bx, by, 1.6, C.inkSoft, 0.55 * fade, 2, 511 + i);
              taper(g, bx, by, bx + 5, by - sweep, 1.6, C.inkSoft, 0.55 * fade, 2, 513 + i);
            }
          }
        },
        muse(t, env) {
          const enter = smooth(t / 0.38);
          const fromXN = env.panel.side < 0 ? 1.1 : -0.1;
          const xn = lerp(fromXN, env.panel.marginXN, enter);
          const gesture = smooth((t - 0.5) / 0.16) * (1 - smooth((t - 0.88) / 0.12));
          return {
            visible: t > 0.02,
            x: xn * env.W,
            y: env.H * 0.875,
            scale: 1,
            face: enter > 0.85 ? env.panel.side : 0,
            pose: { gesture, gestureDir: env.panel.side, bow: 0, observe: enter > 0.9 && gesture < 0.1 ? 0.6 : 0 },
          };
        },
      };

      /* ---------------- SCENE: workshop — The Workshop --------------- */
      SCENES.workshop = {
        far(g, w, h, env) {
          const r = rng(env.seed + 600);
          /* warm parchment wall with tooth and old water stains */
          g.fillStyle = C.parchment;
          g.fillRect(0, 0, w, h);
          scumble(g, 0, 0, w, h * 0.9, C.bone, 0.22, 120, env.seed + 601);
          scumble(g, w * 0.05, h * 0.06, w * 0.3, h * 0.2, C.paleMaroon, 0.08, 30, env.seed + 602);
          for (let i = 0; i < 10; i++) {
            drag(g, r() * w - 30, r() * h * 0.8, w * 0.3, 10 + r() * 14, C.cotton, 0.14, env.seed + 604 + i);
          }
          /* the arched window and its shaft of day */
          const wx = w * 0.74;
          const wy = env.oy + env.H * 0.16;
          const ww = w * 0.11;
          const wh = env.H * 0.3;
          g.fillStyle = C.cotton;
          g.globalAlpha = 0.98;
          g.fillRect(wx - ww / 2, wy + ww / 2, ww, wh - ww / 2);
          g.beginPath();
          g.arc(wx, wy + ww / 2, ww / 2, Math.PI, 0);
          g.fill();
          g.globalAlpha = 1;
          scumble(g, wx - ww / 2, wy, ww, wh, C.parchment, 0.3, 24, env.seed + 606);
          inkline(g, wx - ww / 2, wy + ww / 2, wx - ww / 2, wy + wh, 1.8, 0.5, env.seed + 607);
          inkline(g, wx + ww / 2, wy + ww / 2, wx + ww / 2, wy + wh, 1.8, 0.5, env.seed + 608);
          inkline(g, wx - ww / 2 - 2, wy + wh, wx + ww / 2 + 2, wy + wh + 2, 2, 0.4, env.seed + 609);
          taper(g, wx, wy - ww * 0.1, wx, wy + ww * 0.4, 2, C.bone, 0.7, 3, env.seed + 610);
          for (let i = 0; i < 4; i++) {
            drag(
              g,
              wx - ww * 1.3 - i * w * 0.05,
              wy + wh * 0.3 + i * env.H * 0.08,
              w * 0.16,
              12 + i * 6,
              C.cotton,
              0.18,
              env.seed + 611 + i
            );
          }
          /* pinned working drawings — diagrams of flows, no words */
          for (let d2 = 0; d2 < 3; d2++) {
            const dx = w * (0.1 + d2 * 0.14) + (r() - 0.5) * 14;
            const dy = env.oy + env.H * (0.14 + (d2 % 2) * 0.13);
            const dw = w * 0.09;
            const dh = env.H * 0.1;
            g.save();
            g.translate(dx + dw / 2, dy + dh / 2);
            g.rotate((r() - 0.5) * 0.08);
            g.translate(-dw / 2, -dh / 2);
            g.fillStyle = C.cotton;
            g.globalAlpha = 0.96;
            g.fillRect(0, 0, dw, dh);
            g.globalAlpha = 1;
            inkline(g, 0, 0, dw, 1, 1, 0.3, env.seed + 620 + d2);
            /* a little painted workflow: nodes joined by wobbling lines */
            const nodes = [];
            for (let nn = 0; nn < 4; nn++) nodes.push([dw * (0.15 + r() * 0.7), dh * (0.2 + r() * 0.6)]);
            for (let nn = 0; nn < 3; nn++) inkline(g, nodes[nn][0], nodes[nn][1], nodes[nn + 1][0], nodes[nn + 1][1], 1, 0.55, env.seed + 630 + d2 * 4 + nn);
            nodes.forEach((nd, ni) => {
              g.strokeStyle = C.noir;
              g.globalAlpha = 0.6;
              g.lineWidth = 1.2;
              g.beginPath();
              g.arc(nd[0], nd[1], 3.2, 0, Math.PI * 2);
              g.stroke();
              g.globalAlpha = 1;
              if (ni === 2) daub(g, nd[0], nd[1], 2, 2, 0, C.maroon, 0.8);
            });
            daub(g, dw / 2, 2, 1.8, 1.8, 0, C.noir, 0.85); // the pin
            g.restore();
          }
          /* a timber shelf with the potter's jars */
          const shY = env.oy + env.H * 0.42;
          taper(g, w * 0.07, shY, w * 0.4, shY - 2, 5, C.woodWarm, 0.9, 6, env.seed + 640);
          for (let i = 0; i < 4; i++) {
            const jx = w * (0.11 + i * 0.07);
            daub(g, jx, shY - 9, 6 + r() * 3, 8, 0, i === 1 ? C.rose : C.bone, 0.9);
            inkline(g, jx - 5, shY - 16, jx + 5, shY - 16, 1, 0.3, env.seed + 641 + i);
          }
        },
        mid(g, w, h, env) {
          const r = rng(env.seed + 650);
          const floorY = env.oy + env.H * 0.82;
          /* floor planks */
          g.fillStyle = mix('#f4f2e8', '#3a2422', 0.14);
          g.fillRect(0, floorY, w, h - floorY);
          for (let i = 0; i < 12; i++) {
            drag(g, r() * w - 40, floorY + 8 + r() * (h - floorY - 10), w * 0.4, 3.4, C.umber, 0.16, env.seed + 651 + i);
          }
          inkline(g, 0, floorY + 2, w, floorY, 1.6, 0.3, env.seed + 652);
          /* THE LOOM — warp threads flowing out into a workflow */
          const lx = w * 0.24;
          const lw = w * 0.13;
          const ltop = env.oy + env.H * 0.36;
          taper(g, lx - lw / 2, floorY, lx - lw / 2 - 4, ltop, 8, C.woodWarm, 0.95, 5, env.seed + 660);
          taper(g, lx + lw / 2, floorY, lx + lw / 2 + 4, ltop, 8, C.woodWarm, 0.95, 5, env.seed + 661);
          taper(g, lx - lw / 2 - 8, ltop + 4, lx + lw / 2 + 8, ltop, 6, C.woodWarm, 0.95, 5, env.seed + 662);
          taper(g, lx - lw / 2 - 6, floorY - env.H * 0.12, lx + lw / 2 + 6, floorY - env.H * 0.125, 5, C.woodWarm, 0.9, 5, env.seed + 663);
          for (let i = 0; i < 11; i++) {
            const tx = lx - lw / 2 + (lw / 10) * i;
            taper(g, tx, ltop + 4, tx + (r() - 0.5) * 3, floorY - env.H * 0.125, 1.1, i % 3 === 0 ? C.inkSoft : C.bone, 0.75, 4, env.seed + 664 + i);
          }
          /* the woven cloth gathering at the breast beam */
          g.fillStyle = mix('#f4f2e8', '#630000', 0.35);
          g.globalAlpha = 0.9;
          g.fillRect(lx - lw / 2, floorY - env.H * 0.12, lw, env.H * 0.05);
          g.globalAlpha = 1;
          for (let i = 0; i < 4; i++) {
            drag(g, lx - lw / 2, floorY - env.H * 0.115 + i * env.H * 0.012, lw, 2.4, i % 2 ? C.maroon : C.rose, 0.5, env.seed + 676 + i);
          }
          /* weft threads escape the loom and become a flow diagram */
          for (let i = 0; i < 3; i++) {
            const sy = floorY - env.H * (0.14 + i * 0.05);
            const pts = [[lx + lw / 2 + 6, sy]];
            for (let k2 = 1; k2 <= 5; k2++) {
              pts.push([lx + lw / 2 + 6 + w * 0.07 * k2, sy - env.H * 0.02 * Math.sin(k2 * 1.7 + i * 2.1) - k2 * env.H * 0.012 * i]);
            }
            strokePath(g, pts, 2, C.maroon, 0.6, env.seed + 680 + i);
            const last = pts[pts.length - 1];
            g.strokeStyle = C.noir;
            g.globalAlpha = 0.55;
            g.lineWidth = 1.2;
            g.beginPath();
            g.arc(last[0] + 6, last[1], 4.4, 0, Math.PI * 2);
            g.stroke();
            g.globalAlpha = 1;
          }
          /* painted gear forms resting against the wall */
          const gearAt = (gx, gy, gr, seed2) => {
            g.strokeStyle = C.umber;
            g.globalAlpha = 0.85;
            g.lineWidth = gr * 0.16;
            g.beginPath();
            g.arc(gx, gy, gr, 0, Math.PI * 2);
            g.stroke();
            g.globalAlpha = 1;
            const teeth = 9;
            for (let i2 = 0; i2 < teeth; i2++) {
              const a2 = (i2 / teeth) * Math.PI * 2 + seed2;
              daub(g, gx + Math.cos(a2) * gr * 1.14, gy + Math.sin(a2) * gr * 1.14, gr * 0.14, gr * 0.1, a2, C.umber, 0.8);
            }
            for (let i2 = 0; i2 < 3; i2++) {
              const a2 = (i2 / 3) * Math.PI + seed2;
              taper(g, gx - Math.cos(a2) * gr * 0.8, gy - Math.sin(a2) * gr * 0.8, gx + Math.cos(a2) * gr * 0.8, gy + Math.sin(a2) * gr * 0.8, 2.4, C.umber, 0.7, 3, env.seed + 690 + i2);
            }
            taper(g, gx - gr * 0.7, gy - gr * 0.75, gx + gr * 0.5, gy - gr * 0.9, 2, C.bone, 0.5, 3, env.seed + 694); // rim light
          };
          gearAt(w * 0.52, floorY - env.H * 0.055, env.H * 0.05, 0.4);
          gearAt(w * 0.585, floorY - env.H * 0.028, env.H * 0.032, 1.2);
          /* marble block mid-carve, mallet resting */
          const mx = w * 0.8;
          const mw2 = w * 0.07;
          g.fillStyle = C.bone;
          g.globalAlpha = 0.95;
          g.fillRect(mx - mw2 / 2, floorY - env.H * 0.09, mw2, env.H * 0.09);
          g.globalAlpha = 1;
          g.fillStyle = C.cotton;
          g.globalAlpha = 0.9;
          g.beginPath();
          g.moveTo(mx - mw2 / 2, floorY - env.H * 0.09);
          g.lineTo(mx - mw2 * 0.1, floorY - env.H * 0.125);
          g.lineTo(mx + mw2 / 2, floorY - env.H * 0.09);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          inkline(g, mx - mw2 / 2, floorY - env.H * 0.09, mx + mw2 / 2, floorY - env.H * 0.092, 1.2, 0.4, env.seed + 700);
          inkline(g, mx - mw2 * 0.1, floorY - env.H * 0.123, mx - mw2 * 0.14, floorY - env.H * 0.06, 1, 0.3, env.seed + 701);
          taper(g, mx + mw2 * 0.7, floorY - 4, mx + mw2 * 1.1, floorY - env.H * 0.05, 3, C.woodWarm, 0.85, 3, env.seed + 702);
          /* aqueduct maquette on its table — pipelines in miniature */
          const tx2 = w * 0.645;
          const tyy = floorY - env.H * 0.075;
          taper(g, tx2 - w * 0.05, tyy, tx2 + w * 0.05, tyy - 1, 4, C.woodWarm, 0.9, 4, env.seed + 703);
          taper(g, tx2 - w * 0.042, tyy, tx2 - w * 0.045, floorY, 3, C.woodWarm, 0.85, 3, env.seed + 704);
          taper(g, tx2 + w * 0.042, tyy, tx2 + w * 0.045, floorY, 3, C.woodWarm, 0.85, 3, env.seed + 705);
          for (let i = 0; i < 4; i++) {
            const ax = tx2 - w * 0.036 + i * w * 0.024;
            g.strokeStyle = C.bone;
            g.globalAlpha = 0.9;
            g.lineWidth = 3.4;
            g.beginPath();
            g.arc(ax, tyy - env.H * 0.012, w * 0.01, Math.PI, 0);
            g.stroke();
            g.globalAlpha = 1;
          }
          taper(g, tx2 - w * 0.048, tyy - env.H * 0.026, tx2 + w * 0.048, tyy - env.H * 0.028, 2.6, C.bone, 0.9, 4, env.seed + 706);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 720);
          /* the workbench edge crossing the bottom — repoussoir */
          const by = env.oy + env.H * 0.955;
          g.fillStyle = C.woodWarm;
          g.beginPath();
          g.moveTo(-4, by + 6);
          for (let i = 0; i <= 8; i++) g.lineTo((w * i) / 8, by + Math.sin(i * 1.4 + env.seed) * 4);
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          for (let i = 0; i < 8; i++) {
            drag(g, r() * w - 30, by + 8 + r() * (h - by - 8), w * 0.3, 4, C.umber, 0.4, env.seed + 721 + i);
          }
          /* brush jar and scattered pigment on the bench */
          const jx = w * 0.88;
          daub(g, jx, by + 2, 13, 9, 0, C.bone, 0.95);
          for (let i = 0; i < 4; i++) {
            taper(g, jx - 6 + i * 4, by - 2, jx - 10 + i * 6, by - 22 - r() * 8, 2.2, i % 2 ? C.umber : C.noir, 0.9, 3, env.seed + 730 + i);
          }
          daub(g, w * 0.7, by + 8, 6, 3, 0.4, C.maroon, 0.7);
          daub(g, w * 0.66, by + 12, 4, 2, -0.2, C.rose, 0.7);
          /* the brazier on its legs, coals waiting for the ember */
          const bx = w * 0.085;
          const bzy = env.oy + env.H * 0.93;
          daub(g, bx, bzy, 24, 9, 0, C.noirWarm, 0.95);
          taper(g, bx - 16, bzy + 2, bx - 20, bzy + 26, 3.4, C.noir, 0.9, 3, env.seed + 740);
          taper(g, bx + 16, bzy + 2, bx + 20, bzy + 26, 3.4, C.noir, 0.9, 3, env.seed + 741);
          taper(g, bx, bzy + 4, bx, bzy + 28, 3, C.noirDeep, 0.85, 3, env.seed + 742);
          daub(g, bx - 6, bzy - 4, 5, 3, 0.3, C.umber, 0.9);
          daub(g, bx + 5, bzy - 5, 4.4, 2.6, -0.2, C.umber, 0.9);
          contactShadow(g, bx, bzy + 28, 26, 0.9);
        },
        dynamic(g, env) {
          /* CHERRY EVENT: the forge ember, breathing in the brazier */
          const bx = env.W * 0.085;
          const by = env.H * 0.845;
          const pulse = env.reduced || env.calmAmt > 0.5 ? 0.5 : 0.5 + Math.sin(env.gt * 2.6) * 0.22;
          glow(g, bx, by - 4, 40 + pulse * 14, C.emberHot, 0.12 + pulse * 0.1, 801);
          daub(g, bx - 3, by - 3, 5.5, 3.6, 0.3, C.cherry, 0.9);
          daub(g, bx + 3.4, by - 5, 4.4, 3, -0.2, C.cherry, 0.85);
          daub(g, bx, by - 5, 2.6, 1.8, 0.1, C.emberHot, 0.55 + pulse * 0.35);
          /* one shy spark, now and then */
          if (!env.reduced && env.calmAmt < 0.5) {
            const sp = (env.gt * 0.5) % 1;
            if (sp < 0.55) {
              const su = sp / 0.55;
              daub(g, bx + Math.sin(su * 7) * 5, by - 8 - su * 46, 1.6 * (1 - su), 1.3 * (1 - su), 0, C.emberHot, (1 - su) * 0.7);
            }
          }
          /* motes crossing the window shaft */
          if (env.calmAmt < 1) {
            const r = rng(810);
            for (let i = 0; i < 8; i++) {
              const ph = r();
              const u = env.reduced ? ph : (ph + env.gt * 0.02) % 1;
              const mx2 = env.W * (0.6 + 0.16 * r()) - u * env.W * 0.1;
              const my = env.H * 0.2 + u * env.H * 0.42;
              daub(g, mx2, my, 1.2, 1, 0, C.cotton, 0.35 * (1 - env.calmAmt * 0.8));
            }
          }
        },
        muse(t, env) {
          /* she observes from the margin, hands gathered */
          const arrive = smooth(t / 0.25);
          const gesture = smooth((t - 0.58) / 0.14) * (1 - smooth((t - 0.82) / 0.12)) * 0.7;
          const centerDir = env.panel.marginXN > 0.5 ? -1 : 1;
          return {
            visible: t > 0.03,
            x: env.panel.marginXN * env.W,
            y: env.H * 0.885,
            scale: 0.96,
            face: centerDir,
            pose: { gesture, gestureDir: centerDir, bow: 0, observe: arrive * 0.9 },
          };
        },
      };

      /* ---------------- SCENE: gallery — The Gallery of Works -------- */
      /* The fresco band's frame (normalised to the viewport) — the
         4→5 transition grows this little landscape into the real one. */
      const FRESCO = { x: 0.22, y: 0.1, w: 0.56, h: 0.16 };

      SCENES.gallery = {
        fresco: FRESCO,
        far(g, w, h, env) {
          const r = rng(env.seed + 900);
          /* the marble hall — cotton wall, worked plaster */
          g.fillStyle = C.cotton;
          g.fillRect(0, 0, w, h);
          scumble(g, 0, 0, w, h, C.parchment, 0.24, 130, env.seed + 901);
          for (let i = 0; i < 12; i++) {
            drag(g, r() * w - 40, r() * h, w * 0.34, 9 + r() * 12, r() > 0.5 ? C.bone : C.cottonShadow, 0.12, env.seed + 903 + i);
          }
          /* pilasters setting the room's rhythm */
          const pBase = env.oy + env.H * 0.86;
          for (let i = 0; i < 4; i++) {
            const px2 = w * (0.1 + i * 0.26);
            const pw = w * 0.024;
            g.fillStyle = C.bone;
            g.globalAlpha = 0.8;
            g.fillRect(px2 - pw / 2, env.oy + env.H * 0.06, pw, pBase - env.oy - env.H * 0.06);
            g.globalAlpha = 1;
            taper(g, px2 - pw * 0.4, env.oy + env.H * 0.08, px2 - pw * 0.4, pBase - 6, 2, C.cotton, 0.7, 5, env.seed + 910 + i);
            inkline(g, px2 + pw / 2, env.oy + env.H * 0.08, px2 + pw / 2, pBase - 4, 1.2, 0.3, env.seed + 914 + i);
            g.fillStyle = C.bone;
            g.globalAlpha = 0.85;
            g.fillRect(px2 - pw * 0.9, env.oy + env.H * 0.045, pw * 1.8, env.H * 0.018);
            g.globalAlpha = 1;
          }
          /* the fresco band — a painted landscape within the painting */
          const fx = FRESCO.x * env.W;
          const fy = env.oy + FRESCO.y * env.H;
          const fw = FRESCO.w * env.W;
          const fh = FRESCO.h * env.H;
          g.fillStyle = C.parchment;
          g.globalAlpha = 0.96;
          g.fillRect(fx, fy, fw, fh);
          g.globalAlpha = 1;
          /* .. its miniature coast (no cherry — the seal owns it here) */
          for (let i = 0; i < 6; i++) {
            drag(g, fx + r() * fw * 0.5, fy + fh * (0.1 + r() * 0.3), fw * (0.3 + r() * 0.4), 4 + r() * 5, r() > 0.5 ? C.rose : C.bone, 0.25, env.seed + 920 + i);
          }
          g.fillStyle = C.paleMaroon;
          g.globalAlpha = 0.8;
          g.beginPath();
          g.moveTo(fx, fy + fh * 0.55);
          for (let i = 0; i <= 6; i++) g.lineTo(fx + (fw * i) / 6, fy + fh * (0.55 - 0.2 * Math.abs(Math.sin(i * 1.7 + env.seed))));
          g.lineTo(fx + fw, fy + fh * 0.6);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          for (let i = 0; i < 5; i++) {
            drag(g, fx + r() * fw * 0.6, fy + fh * (0.66 + r() * 0.24), fw * 0.3, 3, i % 2 ? C.seaBone : C.bone, 0.4, env.seed + 927 + i);
          }
          templeFar(g, fx + fw * 0.7, fy + fh * 0.55, fw * 0.08, fh * 0.3, env.seed + 933, C.bone);
          cypress(g, fx + fw * 0.12, fy + fh * 0.86, fh * 0.5, env.seed + 934);
          /* fresco frame — twin ink runs and a bone fillet */
          taper(g, fx - 6, fy - 4, fx + fw + 6, fy - 6, 5, C.umber, 0.85, 6, env.seed + 935);
          taper(g, fx - 6, fy + fh + 5, fx + fw + 6, fy + fh + 3, 5, C.umber, 0.85, 6, env.seed + 936);
          taper(g, fx - 5, fy - 4, fx - 6, fy + fh + 5, 5, C.umber, 0.8, 5, env.seed + 937);
          taper(g, fx + fw + 5, fy - 5, fx + fw + 6, fy + fh + 4, 5, C.umber, 0.8, 5, env.seed + 938);
          inkline(g, fx, fy + 2, fx + fw, fy + 3, 1, 0.3, env.seed + 939);
          /* clerestory light — three cotton shafts falling in */
          for (let i = 0; i < 3; i++) {
            const sx = w * (0.3 + i * 0.24);
            for (let k2 = 0; k2 < 3; k2++) {
              taper(
                g,
                sx + k2 * 10,
                env.oy + env.H * 0.05,
                sx - env.W * 0.06 + k2 * 10,
                env.oy + env.H * 0.75,
                26 - k2 * 6,
                C.cotton,
                0.12,
                5,
                env.seed + 940 + i * 3 + k2
              );
            }
          }
        },
        mid(g, w, h, env) {
          const r = rng(env.seed + 950);
          /* the picture rail and its hanging frames */
          const railY = env.oy + env.H * 0.3;
          inkline(g, w * 0.05, railY, w * 0.95, railY + 2, 2, 0.4, env.seed + 951);
          const hangs = [
            [0.14, 0.1, 0.4],
            [0.44, 0.14, 0.52],
            [0.68, 0.09, 0.38],
            [0.86, 0.08, 0.46],
          ];
          hangs.forEach((hg, i) => {
            const fw = w * hg[1];
            const fh = fw * (0.75 + hash(env.seed + i * 7.7) * 0.5);
            const fx = w * hg[0] - fw / 2;
            const fy = railY + env.H * hg[2] * 0.28;
            /* cords from the rail */
            inkline(g, fx + fw * 0.1, railY + 2, fx + fw * 0.35, fy, 1, 0.4, env.seed + 952 + i);
            inkline(g, fx + fw * 0.9, railY + 2, fx + fw * 0.65, fy, 1, 0.4, env.seed + 956 + i);
            pictureFrame(g, fx, fy, fw, fh, env.seed + 960 + i * 11, i === 1 ? C.noirWarm : C.umber);
          });
          /* the gallery plaque — CHERRY EVENT: its wax seal */
          const plx = w * 0.53;
          const ply = env.oy + env.H * 0.68;
          g.fillStyle = C.bone;
          g.globalAlpha = 0.95;
          g.fillRect(plx - w * 0.05, ply, w * 0.1, env.H * 0.045);
          g.globalAlpha = 1;
          inkline(g, plx - w * 0.05, ply, plx + w * 0.05, ply + 1, 1.2, 0.4, env.seed + 980);
          inkline(g, plx - w * 0.05, ply + env.H * 0.045, plx + w * 0.05, ply + env.H * 0.044, 1.2, 0.4, env.seed + 981);
          /* ornament dashes — engraving, not words */
          for (let i = 0; i < 4; i++) {
            drag(g, plx - w * 0.036, ply + env.H * (0.012 + i * 0.007), w * (0.05 + hash(env.seed + i) * 0.02), 1.4, C.inkSoft, 0.5, env.seed + 982 + i);
          }
          daub(g, plx + w * 0.038, ply + env.H * 0.034, 7, 6.4, 0.3, C.cherry, 0.95);
          daub(g, plx + w * 0.036, ply + env.H * 0.033, 3.4, 3, -0.2, C.maroon, 0.6);
          taper(g, plx + w * 0.033, ply + env.H * 0.037, plx + w * 0.043, ply + env.H * 0.03, 1.4, C.cotton, 0.7, 2, env.seed + 986);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 990);
          /* marble floor sweeping toward the visitor */
          const fy = env.oy + env.H * 0.84;
          g.fillStyle = C.bone;
          g.beginPath();
          g.moveTo(-4, fy + 4);
          for (let i = 0; i <= 8; i++) g.lineTo((w * i) / 8, fy + Math.sin(i * 2.2 + env.seed) * 3);
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          for (let i = 0; i < 16; i++) {
            drag(g, r() * w - 30, fy + 8 + r() * (h - fy - 8), w * 0.3, 5 + r() * 6, r() > 0.5 ? C.cotton : C.cottonShadow, 0.3, env.seed + 991 + i);
          }
          /* perspective joints running to a quiet centre */
          const vx = w * 0.5;
          for (let i = 0; i < 6; i++) {
            const bx = (w * (i + 0.5)) / 6;
            inkline(g, bx, h + 2, lerp(bx, vx, 0.55), fy + 3, 1.4, 0.25, env.seed + 1010 + i);
          }
          inkline(g, 0, fy + env.H * 0.06, w, fy + env.H * 0.055, 1.2, 0.2, env.seed + 1016);
          /* a viewing bench, empty, waiting */
          const bx2 = w * 0.82;
          const by2 = fy + env.H * 0.075;
          taper(g, bx2 - w * 0.05, by2, bx2 + w * 0.05, by2 - 2, 6, C.umber, 0.9, 5, env.seed + 1017);
          taper(g, bx2 - w * 0.042, by2, bx2 - w * 0.046, by2 + env.H * 0.04, 4, C.noirWarm, 0.85, 3, env.seed + 1018);
          taper(g, bx2 + w * 0.042, by2, bx2 + w * 0.046, by2 + env.H * 0.04, 4, C.noirWarm, 0.85, 3, env.seed + 1019);
          contactShadow(g, bx2, by2 + env.H * 0.045, w * 0.05, 0.7);
        },
        dynamic(g, env) {
          /* dust motes hanging in the shafts — they settle to stillness
             while a study is open */
          const r = rng(1030);
          const lively = 1 - env.calmAmt * 0.85;
          for (let i = 0; i < 16; i++) {
            const ph = r();
            const sx = env.W * (0.28 + (i % 3) * 0.24);
            const u = env.reduced ? ph : (ph + env.gt * 0.012 * (0.5 + r() * 0.8) * lively) % 1;
            const mx2 = sx - u * env.W * 0.05 + Math.sin(u * 11 + i) * 6 * lively;
            const my = env.H * 0.1 + u * env.H * 0.62;
            daub(g, mx2, my, 1.1 + r() * 0.8, 1, 0, C.cotton, (0.24 + r() * 0.2) * (0.4 + lively * 0.6));
          }
        },
        muse(t, env) {
          /* the curator — she presents the works, arm unfurled */
          const arrive = smooth(t / 0.22);
          const gesture =
            arrive *
            (0.35 + 0.65 * smooth((t - 0.3) / 0.2) * (1 - smooth((t - 0.85) / 0.12)));
          return {
            visible: t > 0.02,
            x: env.panel.marginXN * env.W,
            y: env.H * 0.9,
            scale: 0.98,
            face: env.panel.side,
            pose: { gesture, gestureDir: env.panel.side, bow: 0, observe: 0 },
          };
        },
      };

      /* ---------------- SCENE: landscape — The Open Landscape -------- */
      SCENES.landscape = {
        far(g, w, h, env) {
          const r = rng(env.seed + 1100);
          const horizon = env.oy + env.H * 0.52;
          paintSky(g, w, h, horizon, env.seed + 1101, 0.4);
          /* CHERRY EVENT: the low sun over the Aegean */
          const sunX = w * 0.72;
          const sunY = env.oy + env.H * 0.4;
          glow(g, sunX, sunY, env.H * 0.11, C.rose, 0.34, env.seed + 1102);
          glow(g, sunX, sunY, env.H * 0.055, C.emberHot, 0.4, env.seed + 1103);
          daub(g, sunX, sunY, env.H * 0.021, env.H * 0.02, 0, C.cherry, 0.85);
          daub(g, sunX - 3, sunY - 2, env.H * 0.009, env.H * 0.008, 0, C.emberHot, 0.8);
          /* distant headland and its temple */
          g.fillStyle = C.fainterMaroon;
          g.globalAlpha = 0.9;
          g.beginPath();
          g.moveTo(-4, horizon + 2);
          for (let i = 0; i <= 10; i++) {
            const x = (w * i) / 10;
            g.lineTo(x, horizon - env.H * 0.07 * Math.max(0, Math.sin(i * 0.9 + 0.4)) * (i < 6 ? 1 : 0.3));
          }
          g.lineTo(w + 4, horizon + 2);
          g.closePath();
          g.fill();
          g.globalAlpha = 1;
          templeFar(g, w * 0.22, horizon - env.H * 0.052, w * 0.05, env.H * 0.045, env.seed + 1105, C.bone);
          /* open water to the horizon */
          paintSea(g, w, horizon + 2, env.oy + env.H * 0.66, env.seed + 1106, sunX);
        },
        mid(g, w, h, env) {
          const r = rng(env.seed + 1120);
          /* the coast shelf — dusty bone ground: mostly cotton and
             bone, maroon kept to thin washes in the hollows */
          const shelfY = env.oy + env.H * 0.64;
          g.fillStyle = mix('#ddd8c6', '#630000', 0.06);
          g.beginPath();
          g.moveTo(-4, shelfY + env.H * 0.04);
          for (let i = 0; i <= 10; i++) g.lineTo((w * i) / 10, shelfY + Math.sin(i * 1.8 + env.seed) * env.H * 0.02);
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          for (let i = 0; i < 26; i++) {
            drag(
              g,
              r() * w - 30,
              shelfY + env.H * 0.03 + r() * (h - shelfY - env.H * 0.04),
              50 + r() * 110,
              5 + r() * 8,
              r() > 0.62 ? C.cotton : r() > 0.24 ? C.bone : C.cottonShadow,
              0.24,
              env.seed + 1121 + i
            );
          }
          /* the thin maroon washes, pooling where the slope shades */
          for (let i = 0; i < 6; i++) {
            drag(
              g,
              r() * w - 40,
              shelfY + env.H * (0.05 + r() * 0.13),
              w * (0.14 + r() * 0.2),
              7 + r() * 9,
              i % 2 ? C.paleMaroon : C.fainterMaroon,
              0.09 + r() * 0.06,
              env.seed + 1230 + i
            );
          }
          scumble(g, 0, shelfY + env.H * 0.02, w, h - shelfY - env.H * 0.02, C.bone, 0.16, 60, env.seed + 1238);
          /* pale terrace walls stepping the hillside */
          for (let tw = 0; tw < 4; tw++) {
            const ty2 = shelfY + env.H * (0.045 + tw * 0.031);
            let tx0 = w * (0.01 + hash(env.seed + tw * 3.3) * 0.09);
            for (let k2 = 0; k2 < 5; k2++) {
              const tx1 = tx0 + w * (0.09 + hash(env.seed + tw * 7.1 + k2 * 1.9) * 0.09);
              const yy2 = ty2 + k2 * env.H * 0.0045 + (hash(env.seed + tw + k2 * 1.7) - 0.5) * 3;
              taper(g, tx0, yy2, tx1, yy2 + env.H * 0.0035, 2.6, C.cotton, 0.55, 4, env.seed + 1240 + tw * 10 + k2);
              taper(g, tx0 + 2, yy2 + 2.6, tx1, yy2 + env.H * 0.0035 + 2.6, 1.5, C.umber, 0.16, 3, env.seed + 1290 + tw * 10 + k2);
              /* the little step down to the next wall run */
              if (k2 < 4) taper(g, tx1, yy2 + env.H * 0.0035, tx1 + 2, yy2 + env.H * 0.009, 1.8, C.cotton, 0.5, 2, env.seed + 1340 + tw * 10 + k2);
              tx0 = tx1 + w * 0.01;
            }
          }
          /* the bone coast road, winding down toward her walking line */
          const roadX = (u) => w * (0.62 - 0.14 * u + 0.13 * Math.sin(u * 5.2) * (0.25 + 0.75 * u));
          const roadYm = (u) => shelfY + env.H * 0.03 + (h - shelfY - env.H * 0.03 + 8) * (0.18 * u + 0.82 * u * u);
          let pxr = roadX(0);
          let pyr = roadYm(0);
          g.lineCap = 'round';
          for (let i = 1; i <= 26; i++) {
            const u = i / 26;
            const nx3 = roadX(u);
            const ny3 = roadYm(u);
            g.strokeStyle = C.bone;
            g.globalAlpha = 0.9;
            g.lineWidth = 4 + 46 * u * u;
            g.beginPath();
            g.moveTo(pxr, pyr);
            g.lineTo(nx3, ny3);
            g.stroke();
            pxr = nx3;
            pyr = ny3;
          }
          g.globalAlpha = 1;
          for (let i = 0; i < 10; i++) {
            const u = 0.12 + r() * 0.85;
            drag(g, roadX(u) - (4 + 20 * u * u), roadYm(u), 8 + 44 * u * u, 2 + 5 * u, r() > 0.5 ? C.cotton : C.parchment, 0.4, env.seed + 1350 + i);
          }
          /* edge stones where the road bends */
          for (let i = 0; i < 8; i++) {
            const u = 0.2 + r() * 0.75;
            const sd = r() > 0.5 ? 1 : -1;
            daub(g, roadX(u) + sd * (4 + 24 * u * u), roadYm(u) + (r() - 0.5) * 4, 2.4 + 4 * u, 1.5 + 2.4 * u, (r() - 0.5) * 0.6, r() > 0.45 ? C.bone : C.cottonShadow, 0.7);
          }
          /* the olive grove — a hazier row along the brow first... */
          for (let i = 0; i < 12; i++) {
            const gx = w * (0.03 + i * 0.082) + (r() - 0.5) * w * 0.05;
            const gy = shelfY + env.H * (0.026 + r() * 0.02);
            const gs = 0.5 + r() * 0.35;
            if (gx > w * 0.8) continue; // keep the goats' rock vignette clear
            taper(g, gx, gy, gx + (r() - 0.5) * 4, gy - 9 * gs, 2.2 * gs, C.umber, 0.7, 2, env.seed + 1130 + i);
            for (let d2 = 0; d2 < 4; d2++) {
              daub(g, gx + (r() - 0.5) * 16 * gs, gy - (9 + r() * 8) * gs, (4.5 + r() * 4) * gs, (3 + r() * 2.4) * gs, r() * 0.8 - 0.4, r() > 0.6 ? C.foliageDark : C.foliage, 0.72);
            }
            if (r() > 0.55) daub(g, gx + (r() - 0.5) * 12 * gs, gy - 13 * gs, 3.4 * gs, 1.8 * gs, -0.3, C.bone, 0.32);
          }
          /* ...then three denser clusters on the slopes, varied warm
             grey-noir silhouettes (never green) */
          const clusters = [
            [0.13, 0.075, 5],
            [0.4, 0.105, 6],
            [0.72, 0.09, 5],
          ];
          clusters.forEach((cl, ci) => {
            /* grove shade pooling beneath the cluster */
            drag(g, w * cl[0] - w * 0.07, shelfY + env.H * (cl[1] + 0.045), w * 0.15, 9, C.paleMaroon, 0.13, env.seed + 1450 + ci);
            for (let i = 0; i < cl[2]; i++) {
              const gx = w * cl[0] + (r() - 0.5) * w * 0.09;
              const gy = shelfY + env.H * (cl[1] + (r() - 0.5) * 0.045);
              olive(g, gx, gy, 0.55 + r() * 0.6, env.seed + 1460 + ci * 20 + i * 3);
            }
          });
          /* rocks stepping to the water, a goat pair upon them */
          const rx2 = w * 0.86;
          const ry2 = shelfY + env.H * 0.05;
          daub(g, rx2, ry2, w * 0.045, env.H * 0.03, 0.2, C.maroonDeep, 0.9);
          daub(g, rx2 + w * 0.04, ry2 + env.H * 0.02, w * 0.035, env.H * 0.024, -0.15, C.umber, 0.9);
          taper(g, rx2 - w * 0.04, ry2 - env.H * 0.02, rx2 + w * 0.02, ry2 - env.H * 0.026, 3, C.bone, 0.5, 4, env.seed + 1140);
          goat(g, rx2 - w * 0.008, ry2 - env.H * 0.026, 1.1, env.seed + 1141, false);
          goat(g, rx2 + w * 0.045, ry2 - env.H * 0.002, 0.85, env.seed + 1142, true);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 1150);
          /* the winding bone coast road she walks — its top edge
             sways with the same curve her footpath follows */
          const roadY = env.oy + env.H * 0.87;
          const edgeY = (u) => roadY - env.H * 0.013 + Math.sin(u * 6.1) * env.H * 0.008 + Math.sin(u * 13.7 + env.seed) * 2;
          g.fillStyle = C.bone;
          g.beginPath();
          g.moveTo(-4, edgeY(0));
          for (let i = 0; i <= 24; i++) g.lineTo((w * i) / 24, edgeY(i / 24));
          g.lineTo(w + 4, edgeY(1));
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          /* the walked surface — cotton dust first, warm and bright */
          for (let i = 0; i < 16; i++) {
            drag(g, r() * w - 40, roadY + r() * env.H * 0.06, 60 + r() * 100, 4 + r() * 6, r() > 0.55 ? C.cotton : r() > 0.2 ? C.parchment : C.cottonShadow, 0.3, env.seed + 1151 + i);
          }
          /* two faint wheel-worn lines following the bends */
          for (let k2 = 0; k2 < 2; k2++) {
            const pts = [];
            for (let i = 0; i <= 12; i++) {
              const u = i / 12;
              pts.push([w * u, edgeY(u) + env.H * (0.02 + k2 * 0.012) + (hash(env.seed + i * 2.3 + k2) - 0.5) * 3]);
            }
            strokePath(g, pts, 2, C.fainterMaroon, 0.35, env.seed + 1190 + k2);
          }
          /* edge stones riding the swaying top edge */
          for (let i = 0; i < 9; i++) {
            const su = 0.04 + i * 0.115 + (r() - 0.5) * 0.02;
            daub(g, w * su, edgeY(su) + (r() - 0.5) * 5, 8 + r() * 7, 4 + r() * 3, (r() - 0.5) * 0.5, r() > 0.45 ? C.cotton : C.cottonShadow, 0.8);
          }
          for (let i = 0; i < 18; i++) {
            const x = r() * w;
            const y = roadY + env.H * 0.05 + r() * (h - roadY - env.H * 0.06);
            for (let b = 0; b < 3; b++) {
              taper(g, x, y, x + (r() - 0.5) * 12, y - 8 - r() * 12, 1.5, r() > 0.5 ? C.foliage : C.umber, 0.65, 2, env.seed + 1160 + i * 3 + b);
            }
          }
          /* one bold foreground rock anchoring the corner */
          daub(g, w * 0.035, h - env.H * 0.02, w * 0.05, env.H * 0.045, 0.3, C.maroonDeep, 0.95);
          taper(g, w * 0.005, h - env.H * 0.05, w * 0.06, h - env.H * 0.062, 3, C.bone, 0.5, 4, env.seed + 1180);
        },
        dynamic(g, env) {
          /* the doves along the road — they burst up as she passes and
             resettle behind her (flight driven by her progress) */
          const roadY2 = env.H * 0.855;
          const perches = [0.3, 0.62];
          const mx2 = env.museX / env.W; // her progress along the road
          perches.forEach((pu, i) => {
            const px2 = env.W * pu;
            const passed = clamp((mx2 - pu + 0.05) * 7, 0, 1); // 0 sitting → 1 flown
            const n = 2;
            for (let d2 = 0; d2 < n; d2++) {
              const off = d2 * 14 - 7;
              if (passed <= 0.01) {
                dove(g, px2 + off, roadY2 - 3, 0.9, 0, true, 1200 + i * 5 + d2);
              } else if (passed < 1) {
                const fu = passed;
                const fx2 = px2 + off + fu * (30 + d2 * 26) * (d2 % 2 ? 1 : -0.6);
                const fy2 = roadY2 - 6 - fu * env.H * (0.24 + d2 * 0.08);
                const flap = env.reduced ? 0.25 : (env.gt * 2.4 + d2 * 0.4) % 1;
                dove(g, fx2, fy2, 0.95, flap, false, 1200 + i * 5 + d2);
              } else {
                /* circling high, almost gone */
                const flap = env.reduced ? 0.25 : (env.gt * 2 + d2) % 1;
                const cx2 = px2 + Math.sin((env.reduced ? 1 : env.gt) * 0.7 + d2 * 2) * 40;
                dove(g, cx2, env.H * (0.16 + d2 * 0.05), 0.7, flap, false, 1200 + i * 5 + d2);
              }
            }
          });
        },
        muse(t, env) {
          /* she walks the coast road with the visitor's scroll */
          const u = smooth(t * 1.04);
          const dir = env.panel.side < 0 ? 1 : -1; // enter from the margin side
          const xn = dir > 0 ? lerp(-0.08, 1.08, u) : lerp(1.08, -0.08, u);
          return {
            visible: t > 0.01 && t < 0.99,
            x: xn * env.W,
            y: env.H * (0.875 + Math.sin(xn * 6.1) * 0.008),
            scale: 0.92,
            face: 0,
            pose: { gesture: 0, gestureDir: 1, bow: 0, observe: 0 },
            road: true,
          };
        },
      };

      /* ---------------- SCENE: sanctuary — The Final Sanctuary ------- */
      SCENES.sanctuary = {
        far(g, w, h, env) {
          const r = rng(env.seed + 1300);
          const horizon = env.oy + env.H * 0.55;
          /* the brightest sky of the film — cotton, barely brushed */
          g.fillStyle = C.cotton;
          g.fillRect(0, 0, w, h);
          for (let i = 0; i < 12; i++) {
            drag(g, r() * w - 40, r() * horizon * 0.85, w * (0.3 + r() * 0.5), 6 + r() * 10, r() > 0.7 ? C.rose : C.parchment, 0.12, env.seed + 1301 + i);
          }
          /* a pale bone sun, high and quiet (its reflection below is
             the scene's cherry) */
          glow(g, w * 0.5, env.oy + env.H * 0.16, env.H * 0.07, C.parchment, 0.5, env.seed + 1315);
          daub(g, w * 0.5, env.oy + env.H * 0.16, env.H * 0.018, env.H * 0.017, 0, C.parchment, 0.95);
          daub(g, w * 0.5, env.oy + env.H * 0.16, env.H * 0.011, env.H * 0.01, 0, C.cotton, 1);
          /* the calm sea */
          paintSea(g, w, horizon, env.oy + env.H * 0.72, env.seed + 1316, w * 0.5);
          inkline(g, 0, horizon, w, horizon + 1, 1, 0.14, env.seed + 1317);
        },
        mid(g, w, h, env) {
          const r = rng(env.seed + 1330);
          /* marble balustrade over the water */
          const railY = env.oy + env.H * 0.7;
          g.fillStyle = C.bone;
          g.globalAlpha = 0.95;
          g.fillRect(w * 0.02, railY, w * 0.96, env.H * 0.016);
          g.globalAlpha = 1;
          inkline(g, w * 0.02, railY + 1, w * 0.98, railY, 1.4, 0.35, env.seed + 1331);
          const posts = 11;
          for (let i = 0; i < posts; i++) {
            if (i === 4) continue; // one post lost to time
            const px2 = w * (0.05 + (i * 0.9) / (posts - 1));
            const ph = env.H * 0.05;
            taper(g, px2, railY + ph + env.H * 0.014, px2, railY + env.H * 0.014, Math.max(w * 0.008, 7), C.bone, 0.95, 4, env.seed + 1332 + i);
            taper(g, px2 - 2, railY + ph, px2 - 2, railY + env.H * 0.02, 2.2, C.cotton, 0.7, 3, env.seed + 1344 + i);
          }
          g.fillStyle = C.bone;
          g.globalAlpha = 0.92;
          g.fillRect(w * 0.02, railY + env.H * 0.064, w * 0.96, env.H * 0.014);
          g.globalAlpha = 1;
          /* two slender columns and a resting architrave fragment */
          doricColumn(g, w * 0.115, railY + env.H * 0.078, env.H * 0.52, Math.max(w * 0.03, 22), env.seed + 1356);
          doricColumn(g, w * 0.245, railY + env.H * 0.078, env.H * 0.52, Math.max(w * 0.03, 22), env.seed + 1357);
          g.fillStyle = C.bone;
          g.globalAlpha = 0.94;
          g.fillRect(w * 0.08, railY - env.H * 0.465, w * 0.2, env.H * 0.026);
          g.globalAlpha = 1;
          inkline(g, w * 0.08, railY - env.H * 0.465, w * 0.28, railY - env.H * 0.467, 1.3, 0.4, env.seed + 1358);
          /* doves at rest along the rail */
          dove(g, w * 0.62, railY - 5, 1, 0, true, env.seed + 1360);
          dove(g, w * 0.68, railY - 4, 0.9, 0, true, env.seed + 1361);
          dove(g, w * 0.655, railY - 12, 0.75, 0, true, env.seed + 1362);
        },
        near(g, w, h, env) {
          const r = rng(env.seed + 1370);
          /* the terrace floor — wide, bright, open */
          const fy = env.oy + env.H * 0.79;
          g.fillStyle = C.cotton;
          g.beginPath();
          g.moveTo(-4, fy + 3);
          for (let i = 0; i <= 8; i++) g.lineTo((w * i) / 8, fy + Math.sin(i * 1.9 + env.seed) * 3);
          g.lineTo(w + 4, h + 4);
          g.lineTo(-4, h + 4);
          g.closePath();
          g.fill();
          for (let i = 0; i < 18; i++) {
            drag(g, r() * w - 40, fy + 8 + r() * (h - fy - 10), w * 0.32, 6 + r() * 7, r() > 0.5 ? C.bone : C.parchment, 0.35, env.seed + 1371 + i);
          }
          /* slab joints, hand-ruled */
          for (let i = 0; i < 5; i++) {
            inkline(g, (w * (i + 0.5)) / 5, h + 2, lerp((w * (i + 0.5)) / 5, w * 0.5, 0.4), fy + 4, 1.3, 0.2, env.seed + 1390 + i);
          }
          inkline(g, 0, fy + env.H * 0.08, w, fy + env.H * 0.075, 1.1, 0.16, env.seed + 1395);
          /* the olive branch offered across the corner */
          const bx = w * 0.9;
          const by = env.oy + env.H * 0.08;
          strokePath(
            g,
            [
              [w + 10, by - 20],
              [bx + w * 0.03, by],
              [bx - w * 0.045, by + env.H * 0.06],
              [bx - w * 0.1, by + env.H * 0.09],
            ],
            5,
            C.umber,
            0.9,
            env.seed + 1396
          );
          for (let i = 0; i < 8; i++) {
            const u = i / 8;
            const lx = lerp(w + 4, bx - w * 0.1, u) + (r() - 0.5) * 10;
            const ly = lerp(by - 16, by + env.H * 0.09, u) + (r() - 0.5) * 8;
            daub(g, lx, ly, 9 + r() * 5, 3.2, -0.6 + u * 0.9, r() > 0.4 ? C.foliage : C.foliageDark, 0.85);
            daub(g, lx + 3, ly - 2, 6 + r() * 3, 2.2, -0.5 + u, C.bone, 0.3);
          }
          /* a few loose petals on the marble */
          for (let i = 0; i < 5; i++) {
            daub(g, r() * w, fy + 10 + r() * (h - fy - 14), 3 + r() * 2, 1.6, r() * Math.PI, C.rose, 0.5);
          }
        },
        dynamic(g, env) {
          /* CHERRY EVENT: the sun's single reflection line on the water */
          const ry2 = env.H * 0.62;
          const shimmer = env.reduced || env.calmAmt > 0.5 ? 0 : Math.sin(env.gt * 1.7) * 0.12;
          taper(
            g,
            env.W * 0.5 - env.W * 0.055,
            ry2,
            env.W * 0.5 + env.W * 0.055,
            ry2 + 1,
            3.2 + shimmer * 8,
            C.cherry,
            0.75 + shimmer,
            5,
            1400
          );
          taper(g, env.W * 0.5 - env.W * 0.02, ry2 + 6, env.W * 0.5 + env.W * 0.03, ry2 + 6.5, 1.8, C.cherry, 0.4 + shimmer * 0.5, 3, 1401);
          /* one dove tracing a slow circle above the terrace */
          if (env.calmAmt < 1) {
            const a2 = (env.reduced ? 0.8 : env.gt * 0.5) % (Math.PI * 2);
            const dx2 = env.W * 0.5 + Math.cos(a2) * env.W * 0.18;
            const dy2 = env.H * 0.24 + Math.sin(a2 * 2) * env.H * 0.03;
            const flap = env.reduced ? 0.25 : (env.gt * 2.2) % 1;
            dove(g, dx2, dy2, 0.85, flap, false, 1402);
          }
        },
        muse(t, env) {
          /* she arrives, turns to the visitor, bows, and offers the way */
          const arrive = smooth(t / 0.3);
          const bow = smooth((t - 0.42) / 0.14) * (1 - smooth((t - 0.72) / 0.16));
          const gesture = smooth((t - 0.7) / 0.16);
          return {
            visible: t > 0.02,
            x: env.panel.marginXN * env.W,
            y: env.H * 0.88,
            scale: 1,
            face: env.panel.side,
            pose: { gesture, gestureDir: env.panel.side, bow, observe: arrive > 0.9 && bow < 0.05 && gesture < 0.05 ? 0.5 : 0 },
          };
        },
      };

      /* =============================================================
         6. TRANSITIONS — five distinct painted boundary crossings.
         Each receives the two composed scenes as offscreen canvases
         and repaints the backdrop for blend amount q in 0..1.
         Never a plain crossfade.
         ============================================================= */
      const drawBuf = (g, buf, W, H) => {
        g.drawImage(buf, 0, 0, buf.width, buf.height, 0, 0, W, H);
      };

      /* 1→2 : the loaded brush — one giant diagonal stroke sweeps the
         next scene across the canvas. */
      const trWipe = (g, bufA, bufB, q, env) => {
        const { W, H } = env;
        drawBuf(g, bufA, W, H);
        const s = smooth(q);
        const span = (W + H) * 1.12;
        const d = s * span;
        /* revealed region behind the stroke front (wobbled diagonal) */
        g.save();
        g.beginPath();
        g.moveTo(-40, -40);
        const steps = 14;
        for (let i = 0; i <= steps; i++) {
          const u = i / steps;
          const fx = d - H * (1 - u) - 60;
          const fy = u * (H + 80) - 40;
          g.lineTo(fx + (hash(i * 13.7 + 3) - 0.5) * 60 * (1 - Math.abs(u - 0.5)), fy);
        }
        g.lineTo(-40, H + 40);
        g.closePath();
        g.clip();
        drawBuf(g, bufB, W, H);
        g.restore();
        /* the stroke itself — loaded bristles riding the front */
        const die = 1 - smooth((q - 0.82) / 0.18); // the brush lifts at the end
        if (die > 0.02) {
          const ang = Math.atan2(1, 1); // sweep direction
          for (let i = 0; i < 12; i++) {
            const u = i / 12;
            const off = (u - 0.5) * H * 1.5;
            const cx2 = d - H * 0.5 + Math.cos(ang + Math.PI / 2) * off - 30;
            const cy2 = H * 0.5 + Math.sin(ang + Math.PI / 2) * off - 30;
            const tone = i % 4 === 0 ? C.noirWarm : i % 3 === 0 ? C.cottonShadow : C.parchment;
            taper(
              g,
              cx2 - Math.cos(ang) * (90 + hash(i * 7.7) * 120),
              cy2 - Math.sin(ang) * (90 + hash(i * 7.7) * 120),
              cx2,
              cy2,
              10 + hash(i * 3.3) * 26,
              tone,
              (0.25 + hash(i * 5.1) * 0.4) * die,
              5,
              1500 + i
            );
          }
          /* flung paint ahead of the stroke */
          for (let i = 0; i < 5; i++) {
            daub(
              g,
              d - H * 0.5 + hash(i * 9.1) * 120,
              H * (0.15 + hash(i * 4.7) * 0.7),
              3 + hash(i * 2.9) * 4,
              2 + hash(i * 6.3) * 3,
              hash(i) * Math.PI,
              C.parchment,
              0.5 * die
            );
          }
        }
      };

      /* 2→3 : the page turn — the sheet lifts from the corner and the
         workshop is already waiting beneath. */
      const trTurn = (g, bufA, bufB, q, env) => {
        const { W, H } = env;
        drawBuf(g, bufB, W, H);
        const s = smooth(q);
        const total = W + H;
        const cc = total * (1 - s); // fold line: x + y = cc
        if (cc <= 2) return;
        /* what remains of the old page */
        g.save();
        g.beginPath();
        g.moveTo(-20, -20);
        const steps = 12;
        for (let i = 0; i <= steps; i++) {
          const u = i / steps;
          const fx = clamp(cc * u, -20, W + 20);
          const fy = cc - fx + (hash(i * 11.3) - 0.5) * 24 * Math.sin(u * Math.PI);
          if (i === 0) g.lineTo(-20, Math.min(cc + 20, H + 20));
          g.lineTo(fx, fy);
        }
        g.lineTo(Math.min(cc + 20, W + 20), -20);
        g.closePath();
        g.clip();
        drawBuf(g, bufA, W, H);
        g.restore();
        /* the lifted flap — parchment underside curling along the fold */
        const p1x = cc <= H ? 0 : cc - H;
        const p1y = cc <= H ? cc : H;
        const p2x = cc <= W ? cc : W;
        const p2y = cc <= W ? 0 : cc - W;
        const fw = Math.min(50 + s * 140, 190);
        const nx2 = 0.7071;
        const ny2 = 0.7071; // fold normal, toward the corner
        g.save();
        g.beginPath();
        g.moveTo(p1x, p1y);
        g.lineTo(p2x, p2y);
        g.lineTo(p2x + nx2 * fw, p2y + ny2 * fw);
        g.lineTo(p1x + nx2 * fw, p1y + ny2 * fw);
        g.closePath();
        g.fillStyle = C.parchment;
        g.globalAlpha = 0.97;
        g.fill();
        g.globalAlpha = 1;
        g.clip();
        /* underside brushwork + the ghost of the old page bleeding through */
        for (let i = 0; i < 8; i++) {
          const u = i / 8;
          taper(
            g,
            lerp(p1x, p2x, u) + nx2 * 8,
            lerp(p1y, p2y, u) + ny2 * 8,
            lerp(p1x, p2x, u) + nx2 * (fw - 8),
            lerp(p1y, p2y, u) + ny2 * (fw - 8),
            5 + hash(i * 3.1) * 8,
            i % 3 === 0 ? C.cottonShadow : C.bone,
            0.3,
            4,
            1600 + i
          );
        }
        g.restore();
        /* fold shadow on the new page + lit edge of the sheet */
        taper(g, p1x - nx2 * 5, p1y - ny2 * 5, p2x - nx2 * 5, p2y - ny2 * 5, 14, C.umber, 0.18, 8, 1610);
        taper(g, p1x - nx2 * 14, p1y - ny2 * 14, p2x - nx2 * 14, p2y - ny2 * 14, 22, C.umber, 0.08, 8, 1611);
        taper(g, p1x, p1y, p2x, p2y, 3, C.cotton, 0.8, 8, 1612);
        taper(g, p1x + nx2 * fw, p1y + ny2 * fw, p2x + nx2 * fw, p2y + ny2 * fw, 2.4, C.cottonShadow, 0.6, 8, 1613);
      };

      /* 3→4 : the pigment wash — maroon floods down over the workshop,
         then drains upward to reveal the gallery. */
      const trWash = (g, bufA, bufB, q, env) => {
        const { W, H } = env;
        const flood = q < 0.5;
        drawBuf(g, flood ? bufA : bufB, W, H);
        const qq = flood ? smooth(q / 0.5) : 1 - smooth((q - 0.5) / 0.5);
        const edgeY = qq * H * 1.18 - H * 0.09;
        if (edgeY <= -H * 0.08) return;
        /* the wash body with an irregular running edge */
        g.beginPath();
        g.moveTo(-10, -10);
        g.lineTo(-10, edgeY);
        const steps = 16;
        for (let i = 0; i <= steps; i++) {
          const u = i / steps;
          g.lineTo(u * (W + 20) - 10, edgeY + (hash(i * 17.3 + 5) - 0.3) * H * 0.07);
        }
        g.lineTo(W + 10, -10);
        g.closePath();
        g.fillStyle = C.maroon;
        g.globalAlpha = 0.985;
        g.fill();
        g.globalAlpha = 1;
        /* streaks inside the wet pigment */
        for (let i = 0; i < 14; i++) {
          const x = (W * (i + 0.5)) / 14 + (hash(i * 3.7) - 0.5) * 40;
          taper(
            g,
            x,
            Math.max(-6, edgeY - H * (0.3 + hash(i * 5.9) * 0.5)),
            x + (hash(i * 7.1) - 0.5) * 20,
            edgeY - 6,
            8 + hash(i * 2.3) * 16,
            i % 3 === 0 ? C.maroonDeep : i % 4 === 0 ? C.noirWarm : mix(MAROON, '#810100', 0.4),
            0.3,
            5,
            1700 + i
          );
        }
        /* drips reaching past the edge */
        for (let i = 0; i < 9; i++) {
          const x = W * (0.06 + i * 0.11) + (hash(i * 9.7) - 0.5) * 30;
          const len = H * (0.04 + hash(i * 4.3) * 0.1) * (flood ? 1 : 0.6);
          const dy2 = edgeY + (hash(i * 6.1) - 0.3) * H * 0.05;
          taper(g, x, dy2, x + (hash(i * 8.9) - 0.5) * 6, dy2 + len, 5 + hash(i * 1.7) * 4, C.maroon, 0.9, 4, 1720 + i);
          daub(g, x + (hash(i * 8.9) - 0.5) * 6, dy2 + len + 2, 3.4, 4.2, 0, C.maroon, 0.85);
        }
        /* the wet lip catching the light */
        taper(g, -6, edgeY - 2, W + 6, edgeY + 2, 4, C.maroonDeep, 0.5, 10, 1730);
      };

      /* 4→5 : the fresco expands — the gallery's painted little coast
         grows until it becomes the world. */
      const trFresco = (g, bufA, bufB, q, env) => {
        const { W, H } = env;
        drawBuf(g, bufA, W, H);
        const s = smooth(smooth(q)); // gentle in, committed out
        const fx = FRESCO.x * W;
        /* Anchor the window where the fresco band was actually blitted,
           not at the raw FRESCO.y * H. The gallery far layer paints the
           band at oy + FRESCO.y*H with oy = round(H*MARGINS.far)*0.5
           (getLayers), and composeScene blits that layer shifted by
           dy = -tt * (H*MARGINS.far); so on screen the band's top is
           round(farM)*0.5 + FRESCO.y*H - tt*farM. env.tt is the source
           scene's timeline t; 0.5 reproduces the old fixed anchor if a
           caller ever omits it. */
        const farM = H * MARGINS.far;
        const srcT = typeof env.tt === 'number' ? env.tt : 0.5;
        const fy = Math.round(farM) * 0.5 + FRESCO.y * H - srcT * farM;
        const fw = FRESCO.w * W;
        const fh = FRESCO.h * H;
        const rx2 = lerp(fx, 0, s);
        const ry2 = lerp(fy, 0, s);
        const rw = lerp(fw, W, s);
        const rh = lerp(fh, H, s);
        g.save();
        g.beginPath();
        /* hand-cut opening — corners slightly out of true while small */
        const wob = 8 * (1 - s);
        g.moveTo(rx2 + hash(31.7) * wob, ry2 + hash(12.3) * wob);
        g.lineTo(rx2 + rw - hash(21.9) * wob, ry2 + hash(15.1) * wob);
        g.lineTo(rx2 + rw - hash(41.3) * wob, ry2 + rh - hash(8.7) * wob);
        g.lineTo(rx2 + hash(27.1) * wob, ry2 + rh - hash(19.9) * wob);
        g.closePath();
        g.clip();
        g.drawImage(bufB, 0, 0, bufB.width, bufB.height, rx2, ry2, rw, rh);
        g.restore();
        /* the frame rides outward and dissolves */
        const fa = 1 - s;
        if (fa > 0.02) {
          taper(g, rx2 - 6, ry2 - 4, rx2 + rw + 6, ry2 - 6, 5, C.umber, 0.85 * fa, 6, 1800);
          taper(g, rx2 - 6, ry2 + rh + 5, rx2 + rw + 6, ry2 + rh + 3, 5, C.umber, 0.85 * fa, 6, 1801);
          taper(g, rx2 - 5, ry2 - 4, rx2 - 6, ry2 + rh + 5, 5, C.umber, 0.8 * fa, 5, 1802);
          taper(g, rx2 + rw + 5, ry2 - 5, rx2 + rw + 6, ry2 + rh + 4, 5, C.umber, 0.8 * fa, 5, 1803);
        }
        /* wall light dims around the growing window */
        if (s > 0.1 && s < 0.96) {
          g.globalAlpha = (1 - s) * 0.5;
          scumble(g, 0, 0, W, ry2, C.bone, 0.5, 20, 1810);
          g.globalAlpha = 1;
        }
      };

      /* 5→6 : the water ripple — the coast dissolves in its own
         reflection and stills into the sanctuary. */
      const trRipple = (g, bufA, bufB, q, env) => {
        const { W, H, dpr } = env;
        drawBuf(g, bufB, W, H);
        const s = smooth(q);
        const inv = 1 - s;
        if (inv <= 0.01) return;
        const seaY = H * 0.6;
        /* the old scene above the waterline, shivering apart */
        const bandH = 12;
        for (let y = 0; y < seaY; y += bandH) {
          const u = y / seaY;
          const amp = (3 + 26 * s) * inv * (0.4 + u * 0.9);
          const dx2 = Math.sin(y * 0.045 + s * 8) * amp + Math.sin(y * 0.013 + s * 3) * amp * 0.6;
          g.globalAlpha = inv * (1 - u * 0.12);
          g.drawImage(bufA, 0, y * dpr, W * dpr, bandH * dpr, dx2, y, W, bandH);
        }
        /* its inverted reflection breaking up in the sea band */
        for (let y = seaY; y < H; y += bandH) {
          const u = (y - seaY) / (H - seaY);
          const srcY = clamp(seaY - (y - seaY) * 0.9, 0, seaY - bandH);
          const amp = (8 + 30 * s) * inv * (0.5 + u);
          const dx2 = Math.sin(y * 0.06 + s * 11) * amp;
          g.globalAlpha = inv * 0.55 * (1 - u * 0.6);
          g.drawImage(bufA, 0, srcY * dpr, W * dpr, bandH * dpr, dx2, y, W, bandH);
        }
        g.globalAlpha = 1;
        /* ripple rings where the two worlds touch */
        for (let i = 0; i < 4; i++) {
          const rr = (0.2 + i * 0.24 + s * 0.3) * W * 0.4;
          g.strokeStyle = C.cotton;
          g.globalAlpha = inv * (0.35 - i * 0.06);
          g.lineWidth = 2.4 - i * 0.4;
          g.beginPath();
          g.ellipse(W * 0.5, seaY + 8 + i * 9, rr, rr * 0.1, 0, 0, Math.PI * 2);
          g.stroke();
        }
        g.globalAlpha = 1;
      };

      const TRANSITIONS = [trWipe, trTurn, trWash, trFresco, trRipple];

      /* =============================================================
         7. THE PROJECTOR — sizing, timeline, caches, render passes,
         loop, audio, debug, teardown.
         ============================================================= */
      const MARGINS = { far: 0.05, mid: 0.11, near: 0.16 };
      const LAYER_KEYS = ['far', 'mid', 'near'];
      const BLEND = 0.18;

      let W = 0;
      let H = 0;
      let dpr = 1;
      let mobile = false;
      let destroyed = false;
      let scenes = [];
      let boundsArr = [0, 1];
      let maxScroll = 1;
      let grainPat = null;
      const layerCache = new Map(); // sceneIndex -> {far, mid, near}
      let bufA = null;
      let bufB = null;
      let aCtx = null;
      let b2Ctx = null;

      const makeGrain = () => {
        const gc = doc.createElement('canvas');
        gc.width = 96;
        gc.height = 96;
        const gg = gc.getContext('2d');
        const img = gg.createImageData(96, 96);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = 128 + Math.round((hash(i * 0.37 + 5) - 0.5) * 58);
          img.data[i] = v;
          img.data[i + 1] = v;
          img.data[i + 2] = v;
          img.data[i + 3] = 255;
        }
        gg.putImageData(img, 0, 0);
        grainPat = bctx.createPattern(gc, 'repeat');
      };

      const fit = () => {
        W = window.innerWidth;
        H = window.innerHeight;
        mobile = W <= 768;
        dpr = Math.min(window.devicePixelRatio || 1, mobile ? 1.25 : 1.5);
        [back, dyn].forEach((c) => {
          c.width = Math.max(1, Math.round(W * dpr));
          c.height = Math.max(1, Math.round(H * dpr));
        });
        if (!bufA) {
          bufA = doc.createElement('canvas');
          bufB = doc.createElement('canvas');
          aCtx = bufA.getContext('2d');
          b2Ctx = bufB.getContext('2d');
        }
        [bufA, bufB].forEach((c) => {
          c.width = Math.max(1, Math.round(W * dpr));
          c.height = Math.max(1, Math.round(H * dpr));
        });
        layerCache.clear();
        if (!grainPat) makeGrain();
      };

      const measure = () => {
        const els = Array.from(sceneRoot.querySelectorAll('[data-atl-scene]'));
        scenes = els.map((el, i) => {
          const name = el.getAttribute('data-atl-scene') || '';
          const beats = parseInt(el.getAttribute('data-atl-beats'), 10) || 0;
          return {
            el,
            name,
            painter: SCENES[name] || SCENES.canvas,
            known: !!SCENES[name],
            panelEl: el.querySelector('.atl-scene__panel') || el,
            seed: i * 997 + 7,
            beats,
            beatItems: beats ? Array.from(el.querySelectorAll('[data-atl-beat-item]')) : null,
            beat: -1,
          };
        });
        const sy = window.scrollY || 0;
        maxScroll = Math.max(1, Math.max(doc.body.scrollHeight, htmlEl.scrollHeight) - H);
        boundsArr = [0];
        for (let i = 1; i < scenes.length; i++) {
          const top = scenes[i].el.getBoundingClientRect().top + sy;
          boundsArr.push(clamp(top - H * 0.42, boundsArr[i - 1] + 2, maxScroll - 2));
        }
        boundsArr.push(maxScroll);
        updateBeats(sy);
      };

      /* Content beats: a scene carrying data-atl-beats choreographs its
         [data-atl-beat-item] children along the master timeline — one
         document scroll, no inner scrolling anywhere. Items before the
         current beat become quiet traces (is-done), the current one is
         fully inked (is-live), later ones wait unseen (is-ahead). Pure
         function of scroll, so reverse playback works; without the
         cinema (no-JS, reduced fallback page) no classes are applied
         and every item simply renders in full. */
      const updateBeats = (y) => {
        for (let i = 0; i < scenes.length; i++) {
          const sc = scenes[i];
          if (!sc.beats || !sc.beatItems || !sc.beatItems.length) continue;
          const span = Math.max(1, boundsArr[i + 1] - boundsArr[i]);
          const t = clamp((clamp(y, 0, maxScroll) - boundsArr[i]) / span, 0, 1);
          const tc = clamp(t / 0.85, 0, 1);
          const beat = Math.min(sc.beats - 1, Math.floor(tc * sc.beats));
          if (beat === sc.beat) continue;
          sc.beat = beat;
          sc.beatItems.forEach((it, k) => {
            it.classList.toggle('is-done', k < beat);
            it.classList.toggle('is-live', k === beat);
            it.classList.toggle('is-ahead', k > beat);
          });
        }
      };

      const timelineAt = (y) => {
        const n = scenes.length;
        if (!n) return { i: 0, t: 0, q: 0, j: -1 };
        const yy = clamp(y, 0, maxScroll);
        let i = 0;
        while (i < n - 1 && yy >= boundsArr[i + 1]) i++;
        const span = Math.max(1, boundsArr[i + 1] - boundsArr[i]);
        const t = clamp((yy - boundsArr[i]) / span, 0, 1);
        let q = 0;
        let j = -1;
        if (i < n - 1 && t > 1 - BLEND) {
          q = smooth((t - (1 - BLEND)) / BLEND);
          j = i + 1;
        }
        return { i, t, q, j };
      };

      const getLayers = (idx) => {
        if (layerCache.has(idx)) {
          const hit = layerCache.get(idx);
          layerCache.delete(idx);
          layerCache.set(idx, hit);
          return hit;
        }
        const sc = scenes[idx];
        const out = {};
        LAYER_KEYS.forEach((k) => {
          const M = Math.round(H * MARGINS[k]);
          const c = doc.createElement('canvas');
          c.width = Math.max(1, Math.round(W * dpr));
          c.height = Math.max(1, Math.round((H + M) * dpr));
          const gg = c.getContext('2d');
          gg.setTransform(dpr, 0, 0, dpr, 0, 0);
          const env = { W, H, oy: M * 0.5, seed: sc.seed, mobile };
          try {
            sc.painter[k](gg, W, H + M, env);
          } catch (err) {
            /* a torn layer must never take the page down */
          }
          out[k] = c;
        });
        /* optional base layer — the physical canvas surface itself:
           full-frame, no parallax margin, never reveal-clipped */
        if (sc.painter.base) {
          const c = doc.createElement('canvas');
          c.width = Math.max(1, Math.round(W * dpr));
          c.height = Math.max(1, Math.round(H * dpr));
          const gg = c.getContext('2d');
          gg.setTransform(dpr, 0, 0, dpr, 0, 0);
          try {
            sc.painter.base(gg, W, H, { W, H, oy: 0, seed: sc.seed, mobile });
          } catch (err) {
            /* a torn layer must never take the page down */
          }
          out.base = c;
        }
        layerCache.set(idx, out);
        while (layerCache.size > 4) layerCache.delete(layerCache.keys().next().value);
        return out;
      };

      const clipReveal = (g, mode, q, seed) => {
        g.beginPath();
        if (mode === 'x') {
          const lead = q * W * 1.15 - W * 0.06;
          g.moveTo(-20, -20);
          for (let i = 0; i <= 12; i++) {
            const u = i / 12;
            g.lineTo(lead + (hash(seed + i * 7.9) - 0.5) * 70 * Math.sin(u * Math.PI + 0.2), u * (H * 1.3 + 40) - 20);
          }
          g.lineTo(-20, H * 1.3 + 20);
        } else if (mode === 'up') {
          const lead = H * 1.26 * (1 - q) - H * 0.06;
          g.moveTo(-20, H * 1.3 + 20);
          for (let i = 0; i <= 12; i++) {
            const u = i / 12;
            g.lineTo(u * (W + 40) - 20, lead + (hash(seed + i * 5.3) - 0.5) * 60 * Math.sin(u * Math.PI + 0.4));
          }
          g.lineTo(W + 20, H * 1.3 + 20);
        } else {
          const d = q * (W + H * 1.3) * 1.12 - 40;
          g.moveTo(-20, -20);
          for (let i = 0; i <= 12; i++) {
            const u = i / 12;
            const fy = u * (H * 1.3 + 40) - 20;
            g.lineTo(d - fy + (hash(seed + i * 3.7) - 0.5) * 60, fy);
          }
          g.lineTo(-20, H * 1.3 + 20);
        }
        g.closePath();
        g.clip();
      };

      const composeScene = (g, idx, tt, forceComplete) => {
        const sc = scenes[idx];
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.fillStyle = C.cotton;
        g.fillRect(0, 0, W, H);
        const L = getLayers(idx);
        if (L.base) g.drawImage(L.base, 0, 0, L.base.width, L.base.height, 0, 0, W, H);
        LAYER_KEYS.forEach((k) => {
          const M = H * MARGINS[k];
          const cv = L[k];
          const dy = -tt * M;
          const rev = sc.painter.reveal && !forceComplete ? sc.painter.reveal(k, tt) : null;
          if (rev && rev.q <= 0.002) return;
          if (rev && rev.q < 0.998) {
            g.save();
            clipReveal(g, rev.mode, rev.q, sc.seed + k.length);
            g.drawImage(cv, 0, 0, cv.width, cv.height, 0, dy, W, H + M);
            g.restore();
          } else {
            g.drawImage(cv, 0, 0, cv.width, cv.height, 0, dy, W, H + M);
          }
        });
      };

      const staticTier = () => !A.motion.allows();

      const renderBackdrop = (y) => {
        const tl = timelineAt(y);
        updateBeats(y);
        if (!scenes.length) {
          bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          bctx.fillStyle = C.cotton;
          bctx.fillRect(0, 0, W, H);
          return tl;
        }
        const complete = staticTier();
        if (tl.q <= 0 || tl.j < 0) {
          composeScene(bctx, tl.i, tl.t, complete);
        } else {
          composeScene(aCtx, tl.i, tl.t, complete);
          composeScene(b2Ctx, tl.j, 0, complete);
          bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          TRANSITIONS[tl.i % TRANSITIONS.length](bctx, bufA, bufB, tl.q, {
            W,
            H,
            dpr,
            seed: scenes[tl.i].seed,
            tt: tl.t /* source scene's timeline t — trFresco needs the blit offset */,
          });
        }
        /* canvas tooth over everything */
        if (grainPat) {
          bctx.save();
          bctx.globalAlpha = 0.07;
          bctx.globalCompositeOperation = 'soft-light';
          bctx.fillStyle = grainPat;
          bctx.fillRect(0, 0, W, H);
          bctx.restore();
        }
        return tl;
      };

      /* ---- audio wiring (always optional-chained, never throws) ---- */
      let lastDom = -1;
      let lastStage = -1;
      let velSm = 0;
      let lastInten = -1;
      let lastIntenAt = -10;

      const audioScene = (domI, domT) => {
        try {
          if (!window.AtelierAudio) {
            /* atelier-audio.js loads defer after us — without this the
               opening setScene lands on nobody and the memo below would
               swallow it forever. Forget the memo so the first frame
               after the engine appears registers the scene. */
            lastDom = -1;
            lastStage = -1;
            return;
          }
          if (domI !== lastDom) {
            if (lastDom !== -1) window.AtelierAudio?.event?.('transition');
            const sc = scenes[domI];
            if (sc && sc.known) window.AtelierAudio?.setScene?.(sc.name);
            lastDom = domI;
            lastStage = -1;
          }
          if (scenes[domI] && scenes[domI].name === 'canvas') {
            const st = canvasStages(domT);
            let stage = 0;
            for (let k = 0; k < st.length; k++) if (st[k].q >= 1) stage = k + 1;
            if (domT > 0.87) stage = 4;
            if (stage !== lastStage) {
              if (lastStage !== -1) window.AtelierAudio?.event?.('brush');
              lastStage = stage;
            }
          }
        } catch (err) {
          /* sound is a garnish, never a failure */
        }
      };

      /* ---- study reveal calming ---- */
      let calmAmt = 0;
      let calmTarget = 0;
      const onStudyOpen = () => {
        calmTarget = 1;
      };
      const onStudyClose = () => {
        calmTarget = 0;
      };
      doc.addEventListener('atl:study-open', onStudyOpen);
      doc.addEventListener('atl:study-close', onStudyClose);

      /* ---- pointer (desktop glance garnish) ---- */
      const finePointer = window.matchMedia('(pointer: fine)').matches;
      let pointerX = -1e4;
      let pointerY = -1e4;
      const onPointer = (e) => {
        pointerX = e.clientX;
        pointerY = e.clientY;
      };
      if (finePointer) window.addEventListener('pointermove', onPointer, { passive: true });

      /* ---- the dynamic pass ---- */
      let gt = 0;
      let lastScrollY = window.scrollY || 0;
      let scrollDelta = 0;
      let lastScrollAt = -10;
      let debugSnap = false;
      let lastPose = { gesture: 0, gestureDir: 1, bow: 0, observe: 0 };

      const renderDynamic = (tl, dt) => {
        dctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        dctx.clearRect(0, 0, W, H);
        if (!scenes.length) return;
        const snap = staticTier() || debugSnap;
        const gtUse = snap ? 0.8 : gt;
        const domI = tl.q > 0.5 && tl.j >= 0 ? tl.j : tl.i;
        const domT = domI === tl.i ? tl.t : 0;

        /* the dominant panel decides which margin is hers */
        const pr = scenes[domI].panelEl.getBoundingClientRect();
        const side = (pr.left + pr.right) / 2 < W / 2 ? -1 : 1; // -1: panel left
        let zL;
        let zR;
        if (side < 0) {
          zL = pr.right + 30;
          zR = W - 24;
        } else {
          zL = 24;
          zR = pr.left - 30;
        }
        const zoneOk = zR - zL > 70;
        const marginXN = zoneOk ? (zL + zR) / 2 / W : side < 0 ? 0.87 : 0.13;
        const panel = { rect: pr, side, marginXN, ok: true };

        const env = {
          W,
          H,
          gt: gtUse,
          t: domT,
          reduced: staticTier(),
          calmAmt,
          panel,
          museX: muse.x,
          scrollDelta,
          sinceScroll: gt - lastScrollAt,
          snap,
          pointerOk: finePointer && !snap,
          px: pointerX,
          py: pointerY,
          mobile,
          now: gtUse,
        };

        const dynOf = (idx, tLocal, alpha) => {
          if (alpha <= 0.02) return;
          const p2 = scenes[idx].painter;
          if (!p2.dynamic) return;
          dctx.save();
          ALPHA_MUL = alpha;
          try {
            p2.dynamic(dctx, Object.assign({}, env, { t: tLocal }));
          } catch (err) {
            /* garnish must never take the frame down */
          }
          ALPHA_MUL = 1;
          dctx.restore();
        };
        if (tl.q > 0 && tl.j >= 0) {
          dynOf(tl.i, tl.t, 1 - tl.q);
          dynOf(tl.j, 0, tl.q);
        } else {
          dynOf(tl.i, tl.t, 1);
        }

        /* ---- the Muse (dominant scene only) ---- */
        const cho = scenes[domI].painter.muse;
        let want = null;
        if (cho) {
          let w2 = null;
          try {
            w2 = cho(domT, env);
          } catch (err) {
            w2 = null;
          }
          if (w2 && w2.visible) {
            const sBase = clamp(H * 0.00155, 0.7, 1.5) * (mobile ? 0.62 : 1);
            const s = sBase * (w2.scale || 1);
            let x = w2.x;
            let y2 = w2.y;
            let ok = true;
            if (mobile) {
              /* smaller, in the lower corner opposite the panel */
              x = side < 0 ? W - 56 : 56;
              y2 = Math.max(y2, H * 0.9);
            }
            const halfW = 52 * s;
            const topY = y2 - 134 * s;
            const overlapV = pr.bottom > topY && pr.top < y2 + 8;
            if (overlapV && !w2.road) {
              const fL = pr.left - halfW - 12;
              const fR = pr.right + halfW + 12;
              if (x > fL && x < fR) x = side < 0 ? clamp(fR, halfW + 8, W - halfW - 8) : clamp(fL, halfW + 8, W - halfW - 8);
              if (x > fL && x < fR) ok = false; // no clear margin — she steps out of frame
            }
            if (!w2.road) x = clamp(x, halfW * 0.6, W - halfW * 0.6);
            want = { visible: true, ok, x, y: y2, scale: s, face: w2.face || 0 };
            lastPose = w2.pose || lastPose;
          }
        }
        updateMuse(muse, want, env, dt || 0.016);
        if (muse.visible && muse.alpha > 0.02) {
          const idle = snap ? true : gt - muse.lastMoveAt > 0.7;
          drawMuse(dctx, muse, lastPose, gtUse, idle);
        }

        audioScene(domI, domT);
      };

      /* ---- render orchestration ---- */
      let renderedY = null;
      let backdropDirty = true;
      let lastTl = null;

      const renderStatic = () => {
        const y = window.scrollY || 0;
        const tl = renderBackdrop(y);
        renderedY = y;
        backdropDirty = false;
        lastTl = tl;
        renderDynamic(tl, 0);
      };

      const frame = (dt) => {
        if (destroyed) return;
        if (debugSnap) return; // hold the deterministic still for QA
        gt += dt;
        /* cheap guard: an editor re-render can detach a measured scene
           element between section events; remeasure once on detection
           (measure() rebinds to the live DOM, so this fires only once
           per detachment, not every frame) */
        for (let gi = 0; gi < scenes.length; gi++) {
          if (!scenes[gi].el.isConnected) {
            measure();
            layerCache.clear();
            backdropDirty = true;
            break;
          }
        }
        const y = window.scrollY || 0;
        scrollDelta = y - lastScrollY;
        if (scrollDelta !== 0) lastScrollAt = gt;
        let tl;
        if (y !== renderedY || backdropDirty) {
          tl = renderBackdrop(y);
          renderedY = y;
          backdropDirty = false;
          lastTl = tl;
        } else {
          tl = lastTl || timelineAt(y);
        }
        renderDynamic(tl, dt);
        lastScrollY = y;

        /* scroll-velocity → ambient intensity (subtle, throttled) */
        try {
          velSm += (Math.abs(scrollDelta) / Math.max(dt, 0.001) - velSm) * Math.min(dt * 3, 1);
          const inten = clamp(velSm / 1600, 0, 1);
          if (Math.abs(inten - lastInten) > 0.06 || gt - lastIntenAt > 0.6) {
            window.AtelierAudio?.setIntensity?.(inten);
            lastInten = inten;
            lastIntenAt = gt;
          }
        } catch (err) {
          /* silence */
        }
        calmAmt += (calmTarget - calmAmt) * Math.min(dt * 2.4, 1);
      };

      /* ---- lifecycle: motion tier, visibility, resize ---- */
      let stopLoop = null;
      let visible = true;
      const applyMotion = () => {
        if (destroyed) return;
        if (A.motion.allows() && visible) {
          if (!stopLoop) stopLoop = A.scheduler.add(frame);
        } else {
          if (stopLoop) {
            stopLoop();
            stopLoop = null;
          }
          calmAmt = calmTarget;
          backdropDirty = true;
          renderStatic();
        }
      };
      const offMotion = A.motion.onChange(() => {
        backdropDirty = true;
        applyMotion();
      });

      let io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            visible = e.isIntersecting;
            if (!visible && stopLoop) {
              stopLoop();
              stopLoop = null;
            } else if (visible) {
              applyMotion();
            }
          });
        });
        io.observe(root);
      }

      const onScroll = A.helpers.onFrame(() => {
        if (destroyed) return;
        if (staticTier()) renderStatic();
      });
      window.addEventListener('scroll', onScroll, { passive: true });

      const onResize = A.helpers.onFrame(() => {
        if (destroyed) return;
        /* Mobile URL-bar collapse fires resize with only a small
           innerHeight delta (width and dpr unchanged). A full fit()
           would resize both canvases and wipe the layer cache — a
           visible repaint hitch mid-scroll — and adopting the new H
           would shift every scene bound (timeline jump). Keep W/H/dpr
           and the canvases as they are; just refresh the document
           bounds. Real resizes / orientation changes still reflow. */
        const nextW = window.innerWidth;
        const nextH = window.innerHeight;
        const nextDpr = Math.min(window.devicePixelRatio || 1, nextW <= 768 ? 1.25 : 1.5);
        if (nextW === W && nextDpr === dpr && Math.abs(nextH - H) < 160) {
          measure(); /* uses the kept H, so boundsArr stays put */
          backdropDirty = true;
          if (staticTier()) renderStatic();
          return;
        }
        fit();
        measure();
        backdropDirty = true;
        if (staticTier()) renderStatic();
      });
      window.addEventListener('resize', onResize, { passive: true });

      /* content height changes (studies opening, editor edits) */
      let ro = null;
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(
          A.helpers.onFrame(() => {
            if (destroyed) return;
            measure();
            backdropDirty = true;
          })
        );
        ro.observe(doc.body);
      }

      /* Shopify editor: this host lives outside the sections it films,
         so a scene section re-render/reorder never unloads us — it just
         leaves detached element refs inside `scenes` (stale rects for
         the Muse margin logic, stale painted layers). Remeasure against
         the live DOM and repaint from scratch. */
      const onSectionChange = () => {
        if (destroyed) return;
        measure();
        layerCache.clear();
        backdropDirty = true;
        if (staticTier()) renderStatic();
      };
      doc.addEventListener('shopify:section:load', onSectionChange);
      doc.addEventListener('shopify:section:reorder', onSectionChange);

      /* ---- QA debug hook (?atlDebug=1 only) ---- */
      let debugCleanup = () => {};
      if (/[?&]atlDebug=1/.test(location.search)) {
        window.AtelierCinemaDebug = {
          get sceneCount() {
            return scenes.length;
          },
          setProgress(p) {
            const y = clamp(Number(p) || 0, 0, 1) * maxScroll;
            /* base.css sets html { scroll-behavior: smooth } — a smooth
               scroll would drift under the still we are about to render.
               Force an instant jump, restoring the inline style after. */
            const prevSB = htmlEl.style.scrollBehavior;
            htmlEl.style.scrollBehavior = 'auto';
            try {
              window.scrollTo({ top: y, left: 0, behavior: 'instant' });
            } catch (err) {
              window.scrollTo(0, y); /* instant anyway: behavior is 'auto' above */
            }
            htmlEl.style.scrollBehavior = prevSB;
            debugSnap = true;
            lastScrollY = y;
            scrollDelta = 0;
            const tl = renderBackdrop(y);
            renderedY = y;
            backdropDirty = false;
            lastTl = tl;
            renderDynamic(tl, 0);
            return y;
          },
        };
        const release = () => {
          debugSnap = false;
        };
        window.addEventListener('wheel', release, { passive: true });
        window.addEventListener('touchmove', release, { passive: true });
        window.addEventListener('keydown', release);
        debugCleanup = () => {
          window.removeEventListener('wheel', release);
          window.removeEventListener('touchmove', release);
          window.removeEventListener('keydown', release);
          if (window.AtelierCinemaDebug) delete window.AtelierCinemaDebug;
        };
      }

      /* ---- first light ---- */
      fit();
      measure();
      applyMotion();
      if (A.motion.allows()) {
        const tl = renderBackdrop(window.scrollY || 0);
        renderedY = window.scrollY || 0;
        backdropDirty = false;
        lastTl = tl;
        renderDynamic(tl, 0);
      }

      /* ---- teardown ---- */
      return () => {
        destroyed = true;
        if (stopLoop) stopLoop();
        offMotion();
        if (io) io.disconnect();
        if (ro) ro.disconnect();
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (finePointer) window.removeEventListener('pointermove', onPointer);
        doc.removeEventListener('atl:study-open', onStudyOpen);
        doc.removeEventListener('atl:study-close', onStudyClose);
        doc.removeEventListener('shopify:section:load', onSectionChange);
        doc.removeEventListener('shopify:section:reorder', onSectionChange);
        debugCleanup();
        try {
          window.AtelierAudio?.setScene?.('none');
        } catch (err) {
          /* silence */
        }
        back.remove();
        dyn.remove();
        layerCache.clear();
        scenes.forEach((sc) => {
          if (sc.beatItems)
            sc.beatItems.forEach((it) => it.classList.remove('is-done', 'is-live', 'is-ahead'));
        });
        htmlEl.classList.remove('atl-cinema-on');
      };
    });
  };

  if (window.Atelier && window.Atelier.__ready) boot(window.Atelier);
  else document.addEventListener('atelier:ready', (e) => boot(e.detail), { once: true });
})();
